import { CommonModule } from "@angular/common";
import { Component, inject, OnInit, ViewChild, ElementRef, AfterViewInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatIconModule } from "@angular/material/icon";
import { ActivatedRoute, Router } from "@angular/router";
import Quill from 'quill';
import Swal from 'sweetalert2';
import { UsersService } from "../../../../core/services/users.service";
import {
  ObjetivoUI,
  ActividadUI,
} from "../../../../core/shared/components/progress-summary/progress-summary.component";
import { ObjectivesService } from "../../../../core/services/objectives.service";
import { TestsService } from "../../../../core/services/tests.service";
import { AppointmentsService } from "../../../../core/services/appointments.service";
import { TEST_TYPES } from "../../../../core/shared/constants";
import { User } from "../../../../core/models/user.interface";
import { AuthService } from '../../../../core/services/auth.service';
import { MatCalendar } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { TimeFormatPipe } from '../../../../core/shared/pipes/time-format.pipe';
import { UsersAssignedPermissionsComponent } from "../users-assigned-permissions/users-assigned-permissions.component";
import { CustomDatePickerComponent } from '../../../../core/shared/components/custom-date-picker/custom-date-picker.component';
import { environment } from '../../../../../environments/environment';
import { RadarChartComponent } from '../../../../core/shared/ui/radar-chart/radar-chart.component';
import { Subject } from 'rxjs';
import { VisionboardService } from '../../../../core/services/visionboard.service';
import { Visionboard, VisionboardImage } from '../../../../core/models/visionboard.interface';
import { ConfigurationService } from '../../../../core/services/configuration.service';

@Component({
  selector: "app-users-assigned-profile",
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, MatCalendar, MatNativeDateModule, TimeFormatPipe, UsersAssignedPermissionsComponent, CustomDatePickerComponent, RadarChartComponent],
  templateUrl: "./users-assigned-profile.component.html",
  styleUrl: "./users-assigned-profile.component.scss",
})
export class UsersAssignedProfileComponent implements OnInit, AfterViewInit {
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private userService = inject(UsersService);
  private objectivesService = inject(ObjectivesService);
  private testsService = inject(TestsService);
  private authService = inject(AuthService);
  private appointmentsService = inject(AppointmentsService);
  private visionboardService = inject(VisionboardService);
  private configurationService = inject(ConfigurationService);

  isLoading = false;
  user: any | null = null; // Información del usuario asignado
  coach: any = null; // Información del coach asignado, si aplica
  proximaCita: string = '';
  selectedDate: Date = new Date();
  citaproxima: any = {}; // Cita más próxima del usuario asignado
  citasFechaSeleccionada: any[] = []; // Citas del usuario asignado para la fecha seleccionada

  // Propiedades para el componente de progreso
  objetivos: ObjetivoUI[] = [];
  objetivosPreview: ObjetivoUI[] = [];
  filteredObjectives: ObjetivoUI[] = [];
  paginatedObjectives: ObjetivoUI[] = [];
  actividades: ActividadUI[] = [];
  autoconocimientoProgress: number = 0;
  estadosFiltro: string[] = ["Todos", "Pendiente", "En progreso", "Completada"];
  expandedObjectiveId: string | null = null;
  objectiveStatusFilter: string = '';
  objectiveDimensionFilter: string = '';
  objectiveDateFromFilter: string = '';
  objectiveDateToFilter: string = '';
  objectiveDimensionOptions: string[] = [];
  showObjectivesModal: boolean = false;
  objectivesItemsPerPage: number = 10;
  objectiveCurrentPage: number = 1;

  // Steps de progreso
  progressSteps: any[] = [];

  // Datos para las tareas
  taskList: any[] = [
    { name: 'Análisis profundo', stage: 'step1', active: false },
    { name: 'Mapa de identidad', stage: 'step2', active: false },
    { name: 'Phoenix Session', stage: 'step3', active: false },
    { name: 'Visionboard', stage: 'step4', active: false },
    { name: 'Phoenix Session', stage: 'step5', active: false },
    { name: 'PRD', stage: 'step6', active: false },
    { name: 'Plan de vida', stage: 'step7', active: false },
    { name: 'Graduación', stage: 'step8', active: false }
  ];

  // Datos para la agenda (dinámicos)
  agendaAppointments: any[] = [];
  userAppointments: any[] = []; // Citas del usuario seleccionado

  // Datos para todas las citas (pendientes y realizadas) - Dinámicos desde BD
  allAppointments: any[] = [];

  // Propiedades para el modal de citas
  showAppointmentsModal: boolean = false;
  filteredAppointments: any[] = [];
  paginatedAppointments: any[] = [];
  statusFilter: string = '';
  dateFromFilter: string = '';
  dateToFilter: string = '';
  currentPage: number = 1;
  itemsPerPage: number = 10;

  // Propiedades para el modal de notas
  showNotesModal: boolean = false;
  selectedAppointment: any = null;
  appointmentNotes: string = '';
  appointmentFiles: any[] = [];
  isSavingNotes: boolean = false;
  selectedFormat: string = 'normal';
  isEditorFocused: boolean = false;
  showPreview: boolean = false;
  
  // Quill editor
  @ViewChild('quillEditor', { static: false }) quillEditor!: ElementRef;
  quillInstance: Quill | null = null;

  // Propiedades para el modal de resultados del círculo de la vida
  showCircleLifeModal: boolean = false;
  circleLifeSeries: any[] = [];
  circleLifeSeriesApply: any[] = [];
  circleLifeCategories: string[] = [];
  circleLifeAnalysisAreas: any[] = [];
  circleLifeEventsSubject: Subject<void> = new Subject<void>();
  priorityArea: string = '';
  opportunityAreas: string = '';
  strengthAreas: string = '';
  circleLifeSeriesIndex: number = 0;
  circleLifeSeriesIndexComparative: number = 2;
  circleLifeAreas: any[] = [
    { 
      name: 'Salud y bienestar', 
      icon: 'assets/icons/saludybienestar.png', 
      expanded: false,
      description: 'Basándote en tu análisis, tu salud y bienestar están en un buen nivel. Considera mantener hábitos saludables y explorar nuevas actividades que mejoren tu bienestar físico y mental.',
      gapClass: '',
      gapClass2: ''
    },
    { 
      name: 'Diversión', 
      icon: 'assets/icons/diversion.png', 
      expanded: false,
      description: 'Tu área de diversión muestra oportunidades de mejora. Busca actividades que realmente disfrutes y te ayuden a desconectar del estrés diario.',
      gapClass: '',
      gapClass2: ''
    },
    { 
      name: 'Crecimiento personal', 
      icon: 'assets/icons/crecimientopersonal.png', 
      expanded: false,
      description: 'El crecimiento personal es clave para tu desarrollo. Identifica áreas específicas donde quieras mejorar y establece metas claras y alcanzables.',
      gapClass: '',
      gapClass2: ''
    },
    { 
      name: 'Círculos sociales', 
      icon: 'assets/icons/circulosocial.png', 
      expanded: false,
      description: 'Tus círculos sociales pueden fortalecerse. Busca conectar con personas que compartan tus intereses y valores.',
      gapClass: '',
      gapClass2: ''
    },
    { 
      name: 'Familia', 
      icon: 'assets/icons/familia.png', 
      expanded: false,
      description: 'La familia es fundamental. Dedica tiempo de calidad con tus seres queridos y fortalece estos vínculos importantes.',
      gapClass: '',
      gapClass2: ''
    },
    { 
      name: 'Transformación', 
      icon: 'assets/icons/transformacion.png', 
      expanded: false,
      description: 'La transformación requiere tiempo y paciencia. Identifica qué cambios quieres hacer y trabaja en ellos paso a paso.',
      gapClass: '',
      gapClass2: ''
    },
    { 
      name: 'Trabajo y desarrollo profesional', 
      icon: 'assets/icons/trabajoydesarrollopersonal.png', 
      expanded: false,
      description: 'Tu desarrollo profesional está en buen camino. Continúa aprendiendo y buscando oportunidades de crecimiento en tu carrera.',
      gapClass: '',
      gapClass2: ''
    },
    { 
      name: 'Ingresos económicos', 
      icon: 'assets/icons/ingreso.png', 
      expanded: false,
      description: 'Los ingresos económicos requieren atención. Considera diversificar tus fuentes de ingreso y mejorar tus habilidades financieras.',
      gapClass: '',
      gapClass2: ''
    }
  ];

  // Propiedades para el modal de análisis profundo
  showAnalisisProfundoModal: boolean = false;
  analisisProfundoTests: any[] = [];
  analisisProfundoLoading: boolean = false;

  ngOnInit(): void {
    this.isLoading = true;
    // Limpiar caché al inicializar
    this.clearCurrentWeekCache();
    
    this.activatedRoute.params.subscribe((params) => {
      if (params["id"]) {
        this.loadUserData(params["id"]);

      } else {
        // Si no hay ID, redirigir a la lista de mentores
        // this.router.navigate(["/dashboard/users-assigned"]);
      }
    });
  }

  ngAfterViewInit(): void {
    // No inicializar Quill aquí, se inicializará cuando se abra el modal
  }

  private loadUserData(userId: string): void {
    // Limpiar caché al cargar nuevos datos de usuario
    this.clearCurrentWeekCache();
    
    // Cargar datos en paralelo
    Promise.all([
      // Cargar datos del usuario
      new Promise<void>((resolve) => {
        this.userService.findById(userId).subscribe({
          next: (res) => {
            this.user = res.data;
            console.log("user", this.user);
            this.obtenerProximaCita(this.user);

            this.obtenerProximaCitaPorFecha(this.selectedDate);
            resolve();
          },
          error: () => {
            resolve();
          },
        });
      }),

      // Cargar tests de análisis profundo para el estado
      new Promise<void>((resolve) => {
        this.testsService.find(userId, TEST_TYPES.Autoconocimiento.id).subscribe({
          next: (res: any) => {
            if (res.data) {
              this.analisisProfundoTests = this.initializeAnalisisProfundoTests(res.data);
              this.updateAnalisisProfundoProgress();
            }
            resolve();
          },
          error: () => {
            resolve();
          }
        });
      }),

      // Cargar objetivos del usuario
      new Promise<void>((resolve) => {
        this.objectivesService.findAll(userId).subscribe({
          next: (response) => {
            if (response && response.data && Array.isArray(response.data)) {
              this.procesarObjetivos(response.data);
              this.extraerActividades();
            }
            resolve();
          },
          error: () => {
            resolve();
          },
        });
      }),

      // Cargar tests de autoconocimiento
      new Promise<void>((resolve) => {
        this.testsService
          .find(userId, TEST_TYPES.Autoconocimiento.id)
          .subscribe({
            next: (response) => {
              if (response && response.data) {
                this.calcularProgresoAutoconocimiento(response.data);
              }
              resolve();
            },
            error: () => {
              resolve();
            },
          });
      }),
    ]).finally(() => {
      this.isLoading = false;
      // Inicializar los steps de progreso después de cargar los datos del usuario
      this.initializeProgressSteps();
      // Cargar los valores de active desde las permissions del usuario
      this.loadTaskPermissions();
      // Cargar las citas del usuario para la agenda
      this.loadUserAppointments();
    });
  }

  goBack(): void {
    this.router.navigate(["/dashboard/users-assigned"]);
  }

  private loadTaskPermissions(): void {
    if (!this.user?.permissions || !Array.isArray(this.user.permissions)) {
      console.log('No hay permissions disponibles o no es un array');
      return;
    }

    console.log('Cargando permissions del usuario:', this.user.permissions);

    // Mapear los valores de active desde las permissions
    this.taskList.forEach(task => {
      const permission = this.user.permissions.find((p: any) => p.stage === task.stage);
      if (permission) {
        task.active = permission.active;
        console.log(`Task ${task.name} (${task.stage}): active = ${permission.active}`);
      } else {
        console.log(`No se encontró permission para ${task.name} (${task.stage})`);
      }
    });

    console.log('TaskList actualizado con permissions:', this.taskList);
  }

  private procesarObjetivos(objetivosOriginales: any[]): void {
    if (!objetivosOriginales || objetivosOriginales.length === 0) {
      this.objetivos = [];
      this.objetivosPreview = [];
      return;
    }

    const sortedObjectives = [...objetivosOriginales].sort(
      (a, b) => this.getObjectiveSortTimestamp(b) - this.getObjectiveSortTimestamp(a)
    );

    this.objetivos = sortedObjectives.map((obj: any) => ({
      _id: obj._id || "",
      nombre: obj.name || "",
      dimension: obj.dimension?.name || "General",
      inicio: this.formatDate(obj.start) || "Sin fecha",
      fin: this.formatDate(obj.end) || "Sin fecha",
      porcentaje: obj.percentage || 0,
      activities: this.procesarActividades(obj.activities),
      state: obj.state,
      startRaw: obj.start || '',
      endRaw: obj.end || ''
    }));

    this.filteredObjectives = [...this.objetivos];
    this.updateObjectivesPagination();
    this.objectiveDimensionOptions = Array.from(
      new Set(this.objetivos.map(obj => obj.dimension).filter(Boolean))
    );
    this.objetivosPreview = this.objetivos.slice(0, 5);
    console.log("Objetivos procesados:", this.objetivos);
  }

  private procesarActividades(activities?: any[]): ActividadUI[] {
    if (!activities || !Array.isArray(activities) || activities.length === 0) {
      return [];
    }

    return activities.map((act) => ({
      nombre: act.name || "",
      descripcion: act.description || "",
      progreso: this.calcularProgresoActividad(act),
      estado: this.obtenerEstadoActividad(act),
      inicio: act.start ? this.formatDate(act.start) : "Sin fecha",
      fin: act.end ? this.formatDate(act.end) : "Sin fecha",
    }));
  }

  private getObjectiveSortTimestamp(obj: any): number {
    if (!obj) {
      return 0;
    }

    const candidates = [obj.updatedAt, obj.createdAt, obj.end, obj.start];
    for (const value of candidates) {
      if (!value) {
        continue;
      }
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return date.getTime();
      }
    }

    return 0;
  }

  private calcularProgresoActividad(actividad: any): number {
    if (!actividad || !actividad.state) return 0;

    switch (actividad.state) {
      case "Terminado":
      case "Realizado":
      case "Completada":
      case "Completado":
        return 100;
      case "Empezado":
      case "En progreso":
        return 50;
      case "Pendiente":
      case "Nuevo":
      case "No empezado":
      default:
        return 0;
    }
  }

  private obtenerEstadoActividad(actividad: any): string {
    if (!actividad || !actividad.state) return "Pendiente";

    switch (actividad.state) {
      case "Empezado":
        return "En progreso";
      case "Terminado":
      case "Realizado":
      case "Completado":
        return "Completada";
      case "No empezado":
        return "Pendiente";
      default:
        return actividad.state;
    }
  }

  private extraerActividades(): void {
    this.actividades = [];

    this.objetivos.forEach((objetivo) => {
      if (objetivo.activities && objetivo.activities.length > 0) {
        this.actividades.push(...objetivo.activities);
      }
    });
  }

  toggleObjective(objetivo: ObjetivoUI, event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    const identifier = objetivo._id || `${objetivo.nombre}-${objetivo.fin}`;
    this.expandedObjectiveId =
      this.expandedObjectiveId === identifier ? null : identifier;
  }

  isObjectiveExpanded(objetivo: ObjetivoUI): boolean {
    const identifier = objetivo._id || `${objetivo.nombre}-${objetivo.fin}`;
    return this.expandedObjectiveId === identifier;
  }

  private normalizeObjectiveState(state: string): 'Pendiente' | 'En progreso' | 'Completado' | 'Sin estado' {
    if (!state) {
      return 'Sin estado';
    }

    const normalized = state.toLowerCase();
    if (normalized.includes('terminado') || normalized.includes('complet')) {
      return 'Completado';
    }
    if (normalized.includes('progreso') || normalized.includes('progress') || normalized.includes('empezado')) {
      return 'En progreso';
    }
    if (normalized.includes('pendiente') || normalized.includes('nuevo')) {
      return 'Pendiente';
    }
    return 'Sin estado';
  }

  formatObjectiveState(state: string): string {
    return this.normalizeObjectiveState(state);
  }

  getObjectiveStatusClass(state: string): string {
    const normalized = this.normalizeObjectiveState(state);
    switch (normalized) {
      case 'Completado':
        return 'status-completed';
      case 'En progreso':
        return 'status-progress';
      case 'Pendiente':
        return 'status-pending';
      default:
        return 'status-default';
    }
  }

  getActivityStatusClass(state: string): string {
    const normalized = this.normalizeObjectiveState(state);
    switch (normalized) {
      case 'Completado':
        return 'activity-completed';
      case 'En progreso':
        return 'activity-progress';
      case 'Pendiente':
        return 'activity-pending';
      default:
        return 'activity-default';
    }
  }

  getActivitiesPreview(objetivo: ObjetivoUI, limit: number = 5): ActividadUI[] {
    if (!objetivo.activities || objetivo.activities.length === 0) {
      return [];
    }
    return objetivo.activities.slice(0, limit);
  }

  openObjectivesModal(): void {
    this.objectiveStatusFilter = '';
    this.objectiveDimensionFilter = '';
    this.objectiveDateFromFilter = '';
    this.objectiveDateToFilter = '';
    this.objectiveCurrentPage = 1;
    this.objectivesItemsPerPage = 10;
    this.objectiveDimensionOptions = Array.from(
      new Set(this.objetivos.map(obj => obj.dimension).filter(Boolean))
    );
    this.filteredObjectives = [...this.objetivos];
    this.updateObjectivesPagination();
    this.showObjectivesModal = true;
  }

  closeObjectivesModal(): void {
    this.showObjectivesModal = false;
    this.objectiveStatusFilter = '';
    this.objectiveDimensionFilter = '';
    this.objectiveDateFromFilter = '';
    this.objectiveDateToFilter = '';
    this.objectiveCurrentPage = 1;
  }

  applyObjectiveFilters(): void {
    let filtered = [...this.objetivos];

    if (this.objectiveStatusFilter) {
      filtered = filtered.filter(
        objective => this.normalizeObjectiveState(objective.state) === this.objectiveStatusFilter
      );
    }

    if (this.objectiveDimensionFilter) {
      const dimensionFilter = this.objectiveDimensionFilter.toLowerCase();
      filtered = filtered.filter(
        objective => (objective.dimension || '').toLowerCase() === dimensionFilter
      );
    }

    if (this.objectiveDateFromFilter) {
      const fromDate = this.parseFlexibleDate(this.objectiveDateFromFilter);
      if (fromDate) {
        filtered = filtered.filter(objective => {
          const startDate = this.parseFlexibleDate(objective.startRaw || '');
          if (!startDate) {
            return false;
          }
          return startDate >= fromDate;
        });
      }
    }

    if (this.objectiveDateToFilter) {
      const toDate = this.parseFlexibleDate(this.objectiveDateToFilter);
      if (toDate) {
        filtered = filtered.filter(objective => {
          const endDate = this.parseFlexibleDate(objective.endRaw || '');
          if (!endDate) {
            return false;
          }
          return endDate <= toDate;
        });
      }
    }

    this.filteredObjectives = filtered;
    this.objectiveCurrentPage = 1;
    this.updateObjectivesPagination();
  }

  updateObjectivesPagination(): void {
    const startIndex = (this.objectiveCurrentPage - 1) * this.objectivesItemsPerPage;
    const endIndex = startIndex + this.objectivesItemsPerPage;
    this.paginatedObjectives = this.filteredObjectives.slice(startIndex, endIndex);
  }

  goToObjectivesPage(page: number): void {
    if (page >= 1 && page <= this.getObjectivesTotalPages()) {
      this.objectiveCurrentPage = page;
      this.updateObjectivesPagination();
    }
  }

  goToObjectivesPageIfNumber(page: number | string): void {
    if (typeof page === 'number') {
      this.goToObjectivesPage(page);
    }
  }

  getObjectivesTotalPages(): number {
    return Math.ceil(this.filteredObjectives.length / this.objectivesItemsPerPage) || 1;
  }

  getObjectivesVisiblePages(): (number | string)[] {
    const totalPages = this.getObjectivesTotalPages();
    const current = this.objectiveCurrentPage;
    const pages: (number | string)[] = [];

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      if (current > 4) {
        pages.push('...');
      }

      const start = Math.max(2, current - 1);
      const end = Math.min(totalPages - 1, current + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (current < totalPages - 3) {
        pages.push('...');
      }

      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  }

  getObjectivesStartIndex(): number {
    return this.filteredObjectives.length === 0
      ? 0
      : (this.objectiveCurrentPage - 1) * this.objectivesItemsPerPage + 1;
  }

  onObjectivesItemsPerPageChange(): void {
    this.objectivesItemsPerPage = Number(this.objectivesItemsPerPage) || 10;
    this.objectiveCurrentPage = 1;
    this.updateObjectivesPagination();
  }

  private calcularProgresoAutoconocimiento(tests: any[]): void {
    console.log("tests", tests);
    if (!tests || !Array.isArray(tests)) {
      this.autoconocimientoProgress = 0;
      return;
    }

    // Contar tests completados por dimensión
    const dimensionsCompleted = tests.filter(
      (test) => test.completed === true
    ).length;
    const dimensionsTotalCount = tests.length;
    console.log("dimensionsCompleted", dimensionsCompleted);

    // Calcular el porcentaje
    this.autoconocimientoProgress =
      dimensionsTotalCount > 0
        ? Math.round((dimensionsCompleted / dimensionsTotalCount) * 100)
        : 0;
  }

  private formatDate(dateStr: string): string {
    if (!dateStr || dateStr.trim() === "") return "Sin fecha";

    try {
      const parsed = this.parseFlexibleDate(dateStr);
      if (!parsed) {
        return "Sin fecha";
      }
      const date = parsed;

      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(today.getDate() + 1);

      if (date.toDateString() === today.toDateString()) {
        return "Hoy";
      } else if (date.toDateString() === tomorrow.toDateString()) {
        return "Mañana";
      } else {
        return `${date.getDate().toString().padStart(2, "0")}/${(
          date.getMonth() + 1
        )
          .toString()
          .padStart(2, "0")}/${date.getFullYear()}`;
      }
    } catch (error) {
      console.error("Error al formatear fecha:", error);
      return "Sin fecha";
    }
  }

  private parseFlexibleDate(value: string | Date): Date | null {
    if (!value) {
      return null;
    }

    if (value instanceof Date) {
      if (!isNaN(value.getTime())) {
        return value;
      }
      return null;
    }

    const trimmed = value.trim();

    // ISO or Date constructor compatible strings
    const direct = new Date(trimmed);
    if (!isNaN(direct.getTime())) {
      return direct;
    }

    // Format DD/MM/YYYY
    const slashMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (slashMatch) {
      const day = parseInt(slashMatch[1], 10);
      const month = parseInt(slashMatch[2], 10) - 1;
      const year = parseInt(slashMatch[3], 10);

      if (month >= 0 && month < 12 && day >= 1 && day <= 31) {
        const date = new Date(year, month, day);
        if (!isNaN(date.getTime())) {
          return date;
        }
      }
    }

    // Format YYYY-MM-DD
    const dashMatch = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (dashMatch) {
      const year = parseInt(dashMatch[1], 10);
      const month = parseInt(dashMatch[2], 10) - 1;
      const day = parseInt(dashMatch[3], 10);
      const date = new Date(year, month, day);
      if (!isNaN(date.getTime())) {
        return date;
      }
    }

    return null;
  }

  // Métodos para manejar eventos del componente
  onCrearObjetivo(): void {
    // En el perfil de usuario asignado, no permitimos crear objetivos
    // Podrías redirigir a una vista específica o mostrar un mensaje
    console.log("Crear objetivo desde perfil de usuario asignado");
  }

  onVerTodosObjetivos(): void {
    this.openObjectivesModal();
  }

  onFiltroChange(estado: string): void {
    // Manejar cambio de filtro si es necesario
    console.log("Filtro cambiado:", estado);
  }

  isUserFreemium(): boolean {
    return !!(
      this.user &&
      this.user.type &&
      this.user.type.suscriptionType &&
      (this.user.type.suscriptionType.id === "1" ||
        this.user.type.suscriptionType.name === "Freemium")
    );
  }

  getProfilePhotoUrl(): string {
    if (this.user?.personalSection?.photoBase64) {
      return this.user.personalSection.photoBase64;
    }
    return this.user?.photo || "assets/img/user-photo-default.png";
  }

  private _currentWeek: number | null = null;

  /**
   * Limpiar caché de la semana actual
   */
  clearCurrentWeekCache(): void {
    this._currentWeek = null;
  }

  getCurrentWeek(): number {
    // Si ya se calculó, devolver el valor en caché
    if (this._currentWeek !== null) {
      return this._currentWeek;
    }

    console.log("getCurrentWeek - user:", this.user);
    console.log("getCurrentWeek - user.createdAt:", this.user?.createdAt);
    
    if (!this.user?.createdAt) {
      console.log("getCurrentWeek - No createdAt found, returning 1 as default");
      this._currentWeek = 1;
      return this._currentWeek;
    }

    const createdAt = new Date(this.user.createdAt);
    const now = new Date();
    
    console.log("getCurrentWeek - createdAt date:", createdAt);
    console.log("getCurrentWeek - now date:", now);
    
    // Normalizar ambas fechas al inicio del día en zona local
    const startOfCreatedAt = new Date(createdAt.getFullYear(), createdAt.getMonth(), createdAt.getDate());
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    // Calcular la diferencia en milisegundos
    const diffInMs = Math.max(0, startOfToday.getTime() - startOfCreatedAt.getTime());
    
    // Convertir a días naturales transcurridos
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    
    console.log("getCurrentWeek - diffInDays:", diffInDays);
    
    // Calcular la semana (redondeando hacia arriba)
    const week = Math.max(1, Math.floor(diffInDays / 7) + 1);
    
    console.log("getCurrentWeek - calculated week:", week);
    
    // Guardar en caché
    this._currentWeek = week;
    return this._currentWeek;
  }

  getUserTitle(): string {
    if (this.user?.type?.coachType?.name) {
      return this.user.type.coachType.name;
    }
    if (this.user?.type?.suscriptionType?.name) {
      return this.user.type.suscriptionType.name;
    }
    return 'Usuario';
  }

  getUserStatus(): string {
    if (this.user?.status?.name) {
      return this.user.status.name.toUpperCase();
    }
    return 'ACTIVO';
  }

  getUserLocation(): string {
    // Por ahora retornamos Lima como valor por defecto
    // En el futuro se podría obtener de los datos del usuario
    return 'Lima';
  }

  getLinkedInUrl(): string {
    if (this.user?.coachProfileData?.contactLinks?.linkedin) {
      return this.user.coachProfileData.contactLinks.linkedin;
    }
    return 'URL de LinkedIn';
  }

  initializeProgressSteps(): void {
    // Limpiar caché para recalcular si es necesario
    this.clearCurrentWeekCache();
    const currentWeek = this.getCurrentWeek();
    console.log("currentWeek", currentWeek);
    const step1Complete = this.user?.stepsAnswers?.step1?.step1Complete === true;
    const step2Complete = this.user?.stepsAnswers?.step2?.step2Complete === true;
    const step3Complete = this.user?.stepsAnswers?.step5?.phoenixSessions3Completed === true;
    const step4Complete = this.user?.stepsAnswers?.step3?.VisionBoardComplete === true;
    const step5Complete = this.user?.stepsAnswers?.step5?.phoenixSessions5Completed === true;
    const step6Complete = this.user?.stepsAnswers?.step4?.rankingCompleted === true;
    const step7Complete = this.user?.stepsAnswers?.step5?.phoenixSessions7Completed === true;
    const step8Complete = this.user?.stepsAnswers?.step5?.phoenixSessions8Completed === true;
    const step9Complete = this.user?.stepsAnswers?.step5?.phoenixSessions9Completed === true;
    
    // Helper function to determine step status
    const getStepStatus = (stepComplete: boolean, weekStart: number, weekEnd: number, stepTitle: string) => {
      const adjustedWeekStart = Math.max(1, weekStart);
      const adjustedWeekEnd = Math.max(adjustedWeekStart, weekEnd);

      const isInRange = currentWeek >= adjustedWeekStart && currentWeek <= adjustedWeekEnd;
      const isAfterRange = currentWeek > adjustedWeekEnd;

      const formatRange = (start: number, end: number) =>
        `Sem ${start}${start !== end ? ` y ${end}` : ''}`;
      
      if (stepComplete && isInRange) {
        return { status: 'completed', statusText: `Culminado - ${formatRange(adjustedWeekStart, adjustedWeekEnd)}` };
      } else if (stepComplete && isAfterRange) {
        return { status: 'completed', statusText: `Completado - ${formatRange(adjustedWeekStart, adjustedWeekEnd)}` };
      } else if (!stepComplete && isInRange) {
        return { status: 'in-progress', statusText: `En curso - ${formatRange(adjustedWeekStart, adjustedWeekEnd)}` };
      } else {
        return { status: 'pending', statusText: `Pendiente - ${formatRange(adjustedWeekStart, adjustedWeekEnd)}` };
      }
    };
    
    this.progressSteps = [
      {
        number: 1,
        title: 'Análisis profundo',
        ...getStepStatus(step1Complete, 0, 2, 'Análisis profundo')
      },
      {
        number: 2,
        title: 'Mapa de identidad',
        ...getStepStatus(step2Complete, 3, 3, 'Mapa de identidad')
      },
      {
        number: 3,
        title: 'Phoenix Session',
        ...getStepStatus(step3Complete, 4, 4, 'Phoenix Session')
      },
      {
        number: 4,
        title: 'Visionboard',
        ...getStepStatus(step4Complete, 5, 5, 'Visionboard')
      },
      {
        number: 5,
        title: 'Phoenix Session',
        ...getStepStatus(step5Complete, 6, 6, 'Phoenix Session')
      },
      {
        number: 6,
        title: 'PRD',
        ...getStepStatus(step6Complete, 7, 9, 'PRD')
      },
      {
        number: 7,
        title: 'Plan de vida',
        ...getStepStatus(step7Complete, 10, 11, 'Plan de vida')
      },
      {
        number: 8,
        title: 'Graduación',
        ...getStepStatus(step8Complete, 12, 12, 'Graduación')
      }
    ];
  }

  onTaskToggle(task: any, event: any): void {
    console.log('Task toggled:', task.name, 'Stage:', task.stage, 'Active:', event.target.checked);
    
    if (!task.stage) {
      console.error('No se encontró el stage para la tarea:', task.name);
      return;
    }

    // Actualizar la permission en el backend
    this.updateUserPermission(task.stage, event.target.checked);
  }

  private updateUserPermission(stage: string, active: boolean): void {
    if (!this.user?._id) {
      console.error('No hay ID de usuario disponible');
      return;
    }

    console.log('Actualizando permission:', {
      userId: this.user._id,
      stage: stage,
      active: active,
      updatedAt: new Date().toISOString()
    });

    // Obtener las permissions actuales del usuario
    const currentPermissions = this.user.permissions || [];
    
    // Buscar si ya existe una permission para este stage
    const existingPermissionIndex = currentPermissions.findIndex((p: any) => p.stage === stage);
    
    if (existingPermissionIndex >= 0) {
      // Actualizar solo la permission existente
      const updatedPermissions = [...currentPermissions];
      updatedPermissions[existingPermissionIndex] = {
        ...updatedPermissions[existingPermissionIndex],
        active: active,
        updatedAt: new Date().toISOString()
      };

      console.log('Actualizando permission existente:', updatedPermissions[existingPermissionIndex]);

      // Llamar al API para actualizar todas las permissions
      this.userService.updatePermissions(this.user._id, updatedPermissions).subscribe({
        next: (response) => {
          console.log('Permission actualizada exitosamente:', response);
          // Actualizar el usuario local con las nuevas permissions
          this.user.permissions = updatedPermissions;
        },
        error: (error) => {
          console.error('Error al actualizar permission:', error);
          // Revertir el estado del toggle si hay error
          const task = this.taskList.find(t => t.stage === stage);
          if (task) {
            task.active = !active;
          }
        }
      });
    } else {
      console.warn('No se encontró la permission para el stage:', stage);
      // Si no existe la permission, crear las permissions por defecto
      this.createDefaultPermissionsIfNeeded(stage, active);
    }
  }

  private createDefaultPermissionsIfNeeded(stage: string, active: boolean): void {
    console.log('Creando permissions por defecto para el stage:', stage);
    
    // Obtener las permissions actuales del usuario
    const currentPermissions = [...(this.user.permissions || [])];
    
    // Crear las permissions por defecto para steps 1-7 (basándose en createDefaultPermissions del backend)
    const defaultPermissions = [];
    const isStep8 = stage === 'step8';
    
    for (let i = 1; i <= 7; i++) {
      const stepStage = `step${i}`;
      // Buscar si ya existe una permission para este step
      const existingPermissionIndex = currentPermissions.findIndex((p: any) => p.stage === stepStage);
      
      if (existingPermissionIndex === -1) {
        // Crear nueva permission por defecto
        defaultPermissions.push({
          id: i.toString(),
          name: `Step ${i}`,
          stage: stepStage,
          active: stepStage === stage ? active : false, // Activar solo si es el stage solicitado
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          updatedBy: this.authService.getAuthUser()?.email || 'system'
        });
      } else {
        // Actualizar la permission existente si es el stage solicitado
        if (stepStage === stage) {
          currentPermissions[existingPermissionIndex] = {
            ...currentPermissions[existingPermissionIndex],
            active: active,
            updatedAt: new Date().toISOString(),
            updatedBy: this.authService.getAuthUser()?.email || 'system'
          };
        }
      }
    }

    // Manejar step8 si es el stage solicitado
    if (isStep8) {
      const existingStep8Index = currentPermissions.findIndex((p: any) => p.stage === 'step8');
      if (existingStep8Index === -1) {
        defaultPermissions.push({
          id: '8',
          name: 'Step 8',
          stage: 'step8',
          active: active,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          updatedBy: this.authService.getAuthUser()?.email || 'system'
        });
      } else {
        currentPermissions[existingStep8Index] = {
          ...currentPermissions[existingStep8Index],
          active: active,
          updatedAt: new Date().toISOString(),
          updatedBy: this.authService.getAuthUser()?.email || 'system'
        };
      }
    } else {
      // Si el stage solicitado no es step1-step8, verificar si necesita crearse
      // (esto es para casos donde el stage no esté en el rango estándar)
      const isStepInRange = /^step[1-7]$/.test(stage);
      if (!isStepInRange) {
        const requestedStageIndex = currentPermissions.findIndex((p: any) => p.stage === stage);
        if (requestedStageIndex === -1) {
          // Crear la permission para el stage solicitado que no está en el rango estándar
          defaultPermissions.push({
            id: stage.replace('step', '') || stage,
            name: `Step ${stage.replace('step', '')}` || stage,
            stage: stage,
            active: active,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            updatedBy: this.authService.getAuthUser()?.email || 'system'
          });
        } else {
          // Actualizar la permission existente
          currentPermissions[requestedStageIndex] = {
            ...currentPermissions[requestedStageIndex],
            active: active,
            updatedAt: new Date().toISOString(),
            updatedBy: this.authService.getAuthUser()?.email || 'system'
          };
        }
      }
    }

    // Combinar todas las permissions: existentes actualizadas + nuevas por defecto + otras existentes
    const allPermissions = [...currentPermissions, ...defaultPermissions];

    console.log('Permissions por defecto creadas:', allPermissions);

    // Guardar todas las permissions en el backend
    this.userService.updatePermissions(this.user._id, allPermissions).subscribe({
      next: (response) => {
        console.log('Permissions por defecto registradas exitosamente:', response);
        // Actualizar el usuario local con las nuevas permissions
        this.user.permissions = allPermissions;
      },
      error: (error) => {
        console.error('Error al registrar permissions por defecto:', error);
        // Revertir el estado del toggle si hay error
        const task = this.taskList.find(t => t.stage === stage);
        if (task) {
          task.active = !active;
        }
      }
    });
  }

  obtenerProximaCita(user: any): void {
    // Cargar información del coach asignado
    this.coach = this.authService.getUser();
    if (!this.coach || !this.coach.coachAppointments) {
      return;
    }

    // Filtrar citas futuras por bookedByUserId
    const futureAppointments = this.coach.coachAppointments.filter(
      (client: any) => client.bookedByUserId === user._id
    );

    if (futureAppointments.length === 0) {
      return;
    }

    // Ordenar las citas por fecha más cercana y mostrar solo la más cercana
    const nextAppointment = futureAppointments.reduce((prev: any, current: any) => {
      const dateA = new Date(`${prev.date}T${prev.time}`);
      const dateB = new Date(`${current.date}T${current.time}`);
      return dateA < dateB ? prev : current;
    });

    console.log("nextAppointment", nextAppointment);
    this.proximaCita = nextAppointment.date;
  }

  // Método para manejar el cambio de fecha en el calendario
  onDateChange(date: Date) {
    // console.log('Fecha seleccionada:', date);
    this.obtenerProximaCitaPorFecha(date);
  }

  obtenerProximaCitaPorFecha(date: Date) {
    let citasFiltradas = [];
    // Cargar información del coach asignado
    this.coach = this.authService.getUser();
    // console.log("coach", this.coach);
    // console.log("this.user", this.user);
    if (this.coach || this.coach.coachAppointments) {
      // Filtrar citas por el usuario asignado
      citasFiltradas = this.coach.coachAppointments.filter(
        (client: any) => client.bookedByUserId === this.user?._id
      )
      // console.log("citasFiltradas", citasFiltradas);
      if (citasFiltradas.length > 0) {
        // ***** Filtrar citas por la fecha seleccionada

        // .toISOString() devuelve la fecha en UTC (+ 5 horas por zona horaria), no en tu zona horaria local .
        // Si estás en una zona como UTC-5 (por ejemplo, Lima), y son por ejemplo las 21:00 del 3 de julio, en UTC ya será el 4 de julio a las 2:00
        // const fechaSeleccionada = date.toISOString().split('T')[0]; // Formato YYYY-MM-DD

        // Solución: obtener la fecha local en formato YYYY-MM-DD
        const fechaSeleccionada = date.toLocaleDateString('en-CA'); // (inglés de Canadá) usa el formato YYYY-MM-DD por defecto
        console.log('Fecha seleccionada:', fechaSeleccionada);

        // filtrar la cita por la fecha seleccionada
        this.citasFechaSeleccionada = citasFiltradas.filter((cita: any) => cita.date === fechaSeleccionada);
        // console.log('Fecha seleccionada:', fechaSeleccionada);
        console.log('this.citasFechaSeleccionada:', this.citasFechaSeleccionada);
        // ordenar citasFechaSelececcionada de forma ascendente por hora
        this.citasFechaSeleccionada.sort((a: any, b: any) => {
          const timeA = new Date(`1970-01-01T${a.time}:00`);
          const timeB = new Date(`1970-01-01T${b.time}:00`);
          return timeA.getTime() - timeB.getTime();
        });
        // console.log('citasFechaSelececcionada ordenadas por hora:', citasFechaSeleccionada);

        // obtener la cita mas proxima de la hora de la fecha seleccionada
        if (this.citasFechaSeleccionada.length > 0) {
          // obtener la hora del sistema en formato 17:00
          const horaActual = new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
          // console.log('Hora actual:', horaActual);
          // obtener la cita mas proxima de la hora actual
          this.citaproxima = this.citasFechaSeleccionada.filter((cita: any) => cita.time >= horaActual);
          console.log('citaproxima filtrada por hora actual:', this.citaproxima[0]);

        }

      }

    }
  }

  // Función para iniciar reunión en Google Meet
  iniciarReunionGoogleMeet(): void {
    // Opción 1: Crear una nueva reunión (más común)
    const googleMeetUrl = 'https://meet.google.com';
    // Opción 2: Si tienes un enlace específico de la cita
    // const googleMeetUrl = this.citaproxima[0]?.meetLink || 'https://meet.google.com/new';

    // Abrir en una nueva pestaña
    window.open(googleMeetUrl, '_blank');
  }

  /**
   * Ver las notas de una cita específica
   */
  viewAppointmentNotes(appointment: any): void {
    console.log('Ver notas de la cita:', appointment);
    this.selectedAppointment = appointment;
    
    // Cargar notas existentes
    this.appointmentNotes = appointment.notes || '';
    this.appointmentFiles = appointment.files || [];
    this.showNotesModal = true;
    
    // Inicializar el editor después de que se abra el modal
    setTimeout(() => {
      this.initializeQuillEditor();
    }, 300);
  }

  /**
   * Inicializar el editor de texto
   */
  initializeEditor(): void {
    const editor = document.querySelector('.rich-editor') as HTMLTextAreaElement;
    if (editor) {
      editor.setAttribute('dir', 'ltr');
      editor.style.direction = 'ltr';
      editor.style.textAlign = 'left';
      editor.style.unicodeBidi = 'normal';
    }
  }

  /**
   * Ingresar a la sesión de una cita (misma funcionalidad que dashboard-coach)
   */
  enterSession(event: any): void {
    console.log('Ingresar a sesión:', event);
    
    // Obtener el objeto appointment del event
    const appointment = event.appointment || event;
    console.log('Appointment obtenido:', appointment);
    
    // Verificar si la sesión es anterior al día actual
    if (this.isSessionInPast(appointment)) {
      this.showAlert('Sesión no disponible', 'No puedes ingresar a sesiones anteriores.', 'warning');
      return;
    }
    
    // Los appointments del backend no tienen campo status, siempre generar enlace de Meet
    const meetingLink = this.generateMeetingLink(appointment);
    if (meetingLink) {
      window.open(meetingLink, '_blank');
    } else {
      this.showAlert('Sesión no disponible', 'La sesión no está disponible aún.', 'warning');
    }
  }

  /**
   * Verificar si la sesión es anterior al momento actual
   */
  isSessionInPast(appointment: any): boolean {
    if (!appointment.date || !appointment.time) {
      return false;
    }
    
    const now = new Date();
    
    // Limpiar la hora de AM/PM si existe para crear el datetime correctamente
    let cleanTime = appointment.time;
    if (cleanTime.includes('AM') || cleanTime.includes('PM')) {
      cleanTime = cleanTime.replace(/\s*(AM|PM)/i, '');
    }
    
    // Crear fecha y hora de la cita
    const appointmentDateTime = new Date(`${appointment.date}T${cleanTime}`);
    
    // Verificar si la fecha es válida
    if (isNaN(appointmentDateTime.getTime())) {
      return false;
    }
    
    // Verificar si la fecha/hora de la cita es anterior al momento actual
    return appointmentDateTime < now;
  }

  /**
   * Generar enlace de Meet usando la lógica del dashboard-coach
   */
  private generateMeetingLink(appointment: any): string {
    if (!appointment._id) {
      console.error('ID de cita no disponible');
      return '';
    }
    
    // Usar el ID de la cita como meetingId, igual que en dashboard-coach
    const meetingId = appointment._id;
    const meetingLink = `${environment.meetingUrl}${meetingId}`;
    
    console.log('🔗 Enlace de Meet generado:', meetingLink);
    return meetingLink;
  }

  /**
   * Mostrar modal de alerta personalizado (misma funcionalidad que dashboard-coach)
   */
  showAlert(title: string, message: string, type: 'info' | 'warning' | 'error' | 'success' = 'info'): void {
    // Implementación simple de alerta - puedes expandir esto con un modal personalizado
    alert(`${title}: ${message}`);
  }

  /**
   * Cargar las citas realizadas del usuario seleccionado
   */
  loadCompletedAppointments(): void {
    // TODO: Implementar la carga real de citas desde el backend
    // Por ahora usamos datos de ejemplo
    console.log('Cargando citas realizadas para el usuario:', this.user?.name);
    
    // Aquí deberías hacer una llamada al servicio para obtener las citas completadas
    // this.userService.getCompletedAppointments(this.user._id).subscribe({
    //   next: (appointments) => {
    //     this.completedAppointments = appointments;
    //   },
    //   error: (error) => {
    //     console.error('Error al cargar citas realizadas:', error);
    //   }
    // });
  }

  // Métodos para la agenda (basados en dashboard-executive)
  loadUserAppointments(): void {
    if (!this.user || !this.user._id) {
      console.warn('No hay usuario seleccionado para cargar citas');
      this.agendaAppointments = [];
      return;
    }

    // Obtener el coach actual
    this.coach = this.authService.getUser();
    if (!this.coach || !this.coach._id) {
      console.warn('No hay coach autenticado');
      this.agendaAppointments = [];
      return;
    }

    console.log('Cargando citas para el usuario:', this.user.name, 'con el coach:', this.coach.name);
    
    // Obtener citas del usuario desde el backend
    this.appointmentsService.findByCustomer(this.user._id).subscribe({
      next: (response) => {
        console.log('📅 Citas del usuario cargadas:', response);
        
        if (response && response.data && Array.isArray(response.data)) {
          // Filtrar solo las citas que pertenecen al coach actual
          const filteredAppointments = response.data.filter(appointment => {
            return appointment.coach && appointment.coach.id === this.coach._id;
          });
          
          console.log('📅 Citas filtradas por coach:', filteredAppointments.length, 'de', response.data.length);
          this.userAppointments = filteredAppointments;
          this.allAppointments = filteredAppointments; // Cargar datos dinámicos en la tabla
          this.agendaAppointments = this.getEventsByDay();
        } else {
          this.userAppointments = [];
          this.allAppointments = [];
          this.agendaAppointments = [];
        }
      },
      error: (error) => {
        console.error('Error al cargar citas del usuario:', error);
        this.userAppointments = [];
        this.allAppointments = [];
        this.agendaAppointments = [];
      }
    });
  }

  getEventsByDay(): any[] {
    if (!this.userAppointments || this.userAppointments.length === 0) {
      return [];
    }

    console.log('📅 Agrupando citas por día - Total de citas:', this.userAppointments.length);

    // Limitar a máximo 10 citas (ordenadas por fecha)
    const limitedAppointments = this.userAppointments
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .slice(0, 10);

    console.log('📅 Citas limitadas a:', limitedAppointments.length);

    // Agrupar eventos por día
    const eventsByDay: { [key: string]: any } = {};
    
    limitedAppointments.forEach(appointment => {
      console.log('📅 Procesando cita con fecha:', appointment.date);
      
      // Crear fecha local sin problemas de zona horaria
      let appointmentDate: Date;
      if (appointment.date.includes('-')) {
        // Formato YYYY-MM-DD
        const dateParts = appointment.date.split('-');
        appointmentDate = new Date(
          parseInt(dateParts[0]), 
          parseInt(dateParts[1]) - 1, 
          parseInt(dateParts[2])
        );
      } else {
        // Otros formatos, usar Date normal
        appointmentDate = new Date(appointment.date);
      }
      
      console.log('📅 Fecha parseada:', appointmentDate);
      const dayKey = appointmentDate.toDateString();
      
      if (!eventsByDay[dayKey]) {
        eventsByDay[dayKey] = {
          dayName: this.getEventDayName(appointmentDate),
          date: this.formatEventDate(appointmentDate),
          dateObj: appointmentDate, // Guardar la fecha para ordenar
          events: []
        };
        console.log('📅 Nuevo día creado:', eventsByDay[dayKey].dayName, eventsByDay[dayKey].date);
      }
      
      eventsByDay[dayKey].events.push({
        time: appointment.time,
        type: 'Sesión de coaching',
        title: `Sesión con ${this.coach?.name || 'Coach'}`,
        // Pasar el objeto appointment completo para tener acceso al ID
        appointment: appointment
      });
    });

    // Convertir a array y ordenar por fecha
    const result = Object.values(eventsByDay).sort((a: any, b: any) => {
      // Ordenar usando el dateObj guardado
      return a.dateObj.getTime() - b.dateObj.getTime();
    });
    
    console.log(`📅 Citas agrupadas en ${result.length} días diferentes (máximo 10 citas)`);
    result.forEach((day, index) => {
      console.log(`📅 Día ${index + 1}: ${day.dayName}, ${day.date} - ${day.events.length} cita(s)`);
    });
    
    return result;
  }

  getEventDayName(date: Date): string {
    const days = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
    return days[date.getDay()];
  }

  formatEventDate(date: Date): string {
    const day = String(date.getDate()).padStart(2, '0');
    const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${day} de ${month}, ${year}`;
  }

  toggleDayMenu(date: string): void {
    // Implementar lógica para mostrar menú del día
    console.log('Toggle menu for date:', date);
  }

  getEventLineClass(eventType: string): string {
    // Asignar colores según el tipo de evento
    if (eventType.toLowerCase().includes('reunión') || eventType.toLowerCase().includes('fabrica') || eventType.toLowerCase().includes('interna')) {
      return 'line-green';
    } else if (eventType.toLowerCase().includes('actividad') || eventType.toLowerCase().includes('integracion')) {
      return 'line-blue';
    } else if (eventType.toLowerCase().includes('coaching') || eventType.toLowerCase().includes('sesión')) {
      return 'line-purple';
    }
    return 'line-default';
  }

  /**
   * Abrir modal de lista completa de citas
   */
  openAppointmentsModal(): void {
    this.showAppointmentsModal = true;
    this.filteredAppointments = [...this.allAppointments];
    this.currentPage = 1;
    this.updatePaginatedAppointments();
  }

  /**
   * Cerrar modal de lista completa de citas
   */
  closeAppointmentsModal(): void {
    this.showAppointmentsModal = false;
    this.statusFilter = '';
    this.dateFromFilter = '';
    this.dateToFilter = '';
    this.currentPage = 1;
  }

  /**
   * Filtrar citas según los filtros aplicados
   */
  filterAppointments(): void {
    let filtered = [...this.allAppointments];

    // Filtrar por estado
    if (this.statusFilter) {
      filtered = filtered.filter(appointment => {
        const isPast = this.isSessionInPast(appointment);
        if (this.statusFilter === 'Pendiente') {
          return !isPast;
        } else if (this.statusFilter === 'Realizada') {
          return isPast;
        }
        return true;
      });
    }

    // Filtrar por fecha desde
    if (this.dateFromFilter) {
      filtered = filtered.filter(appointment => {
        const appointmentDate = new Date(appointment.date);
        const fromDate = new Date(this.dateFromFilter);
        return appointmentDate >= fromDate;
      });
    }

    // Filtrar por fecha hasta
    if (this.dateToFilter) {
      filtered = filtered.filter(appointment => {
        const appointmentDate = new Date(appointment.date);
        const toDate = new Date(this.dateToFilter);
        return appointmentDate <= toDate;
      });
    }

    this.filteredAppointments = filtered;
    this.currentPage = 1;
    this.updatePaginatedAppointments();
  }

  /**
   * Actualizar las citas paginadas
   */
  updatePaginatedAppointments(): void {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    this.paginatedAppointments = this.filteredAppointments.slice(startIndex, endIndex);
  }

  /**
   * Ir a una página específica
   */
  goToPage(page: number): void {
    if (page >= 1 && page <= this.getTotalPages()) {
      this.currentPage = page;
      this.updatePaginatedAppointments();
    }
  }

  /**
   * Ir a una página solo si es un número válido
   */
  goToPageIfNumber(page: number | string): void {
    if (typeof page === 'number') {
      this.goToPage(page);
    }
  }

  /**
   * Obtener el número total de páginas
   */
  getTotalPages(): number {
    return Math.ceil(this.filteredAppointments.length / this.itemsPerPage);
  }

  /**
   * Obtener las páginas visibles para la paginación
   */
  getVisiblePages(): (number | string)[] {
    const totalPages = this.getTotalPages();
    const current = this.currentPage;
    const pages: (number | string)[] = [];

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      
      if (current > 4) {
        pages.push('...');
      }
      
      const start = Math.max(2, current - 1);
      const end = Math.min(totalPages - 1, current + 1);
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      if (current < totalPages - 3) {
        pages.push('...');
      }
      
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  }

  /**
   * Obtener el índice de inicio para mostrar en la paginación
   */
  getStartIndex(): number {
    return (this.currentPage - 1) * this.itemsPerPage + 1;
  }

  /**
   * Manejar cambio en items por página
   */
  onItemsPerPageChange(): void {
    this.currentPage = 1;
    this.updatePaginatedAppointments();
  }

  // ==================== MÉTODOS PARA EL MODAL DE NOTAS ====================

  /**
   * Cerrar modal de notas
   */
  closeNotesModal(): void {
    this.showNotesModal = false;
    this.selectedAppointment = null;
    this.appointmentNotes = '';
    this.appointmentFiles = [];
    this.isEditorFocused = false;
    this.showPreview = false;
    
    // Limpiar editor Quill
    if (this.quillInstance) {
      this.quillInstance.setText('');
      this.quillInstance = null;
    }
  }

  /**
   * Manejar cambio en las notas del editor
   */
  onNotesChange(event: any): void {
    const content = event.target.value || '';
    this.appointmentNotes = content;
  }

  /**
   * Manejar foco del editor
   */
  onEditorFocus(): void {
    this.isEditorFocused = true;
  }


  /**
   * Manejar pérdida de foco del editor
   */
  onEditorBlur(): void {
    this.isEditorFocused = false;
  }

  /**
   * Formatear texto en el editor (funcionalidades básicas para textarea)
   */
  formatText(command: string): void {
    const textarea = document.querySelector('.rich-editor') as HTMLTextAreaElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = textarea.value.substring(start, end);
    let newText = '';

    switch (command) {
      case 'bold':
        newText = `**${selectedText}**`;
        break;
      case 'italic':
        newText = `*${selectedText}*`;
        break;
      case 'underline':
        newText = `__${selectedText}__`;
        break;
      case 'strikethrough':
        newText = `~~${selectedText}~~`;
        break;
      case 'bulletList':
        const lines = selectedText.split('\n');
        newText = lines.map(line => line.trim() ? `• ${line}` : line).join('\n');
        break;
      case 'orderedList':
        const orderedLines = selectedText.split('\n');
        newText = orderedLines.map((line, index) => 
          line.trim() ? `${index + 1}. ${line}` : line
        ).join('\n');
        break;
      case 'alignLeft':
        // Para textarea, solo agregamos comentario
        newText = `[ALINEAR IZQUIERDA]\n${selectedText}`;
        break;
      case 'alignCenter':
        newText = `[ALINEAR CENTRO]\n${selectedText}`;
        break;
      case 'alignRight':
        newText = `[ALINEAR DERECHA]\n${selectedText}`;
        break;
      case 'justify':
        newText = `[JUSTIFICAR]\n${selectedText}`;
        break;
      default:
        newText = selectedText;
    }

    // Reemplazar el texto seleccionado
    const newValue = textarea.value.substring(0, start) + newText + textarea.value.substring(end);
    textarea.value = newValue;
    this.appointmentNotes = newValue;

    // Restaurar la selección
    const newStart = start + newText.length;
    textarea.setSelectionRange(newStart, newStart);
    textarea.focus();
  }

  /**
   * Verificar si un formato está activo (simulado para textarea)
   */
  isFormatActive(command: string): boolean {
    const textarea = document.querySelector('.rich-editor') as HTMLTextAreaElement;
    if (!textarea) return false;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = textarea.value.substring(start, end);

    switch (command) {
      case 'bold':
        return selectedText.startsWith('**') && selectedText.endsWith('**');
      case 'italic':
        return selectedText.startsWith('*') && selectedText.endsWith('*') && !selectedText.startsWith('**');
      case 'underline':
        return selectedText.startsWith('__') && selectedText.endsWith('__');
      case 'strikethrough':
        return selectedText.startsWith('~~') && selectedText.endsWith('~~');
      default:
        return false;
    }
  }

  /**
   * Insertar enlace
   */
  insertLink(): void {
    const url = prompt('Ingrese la URL:');
    if (url) {
      const textarea = document.querySelector('.rich-editor') as HTMLTextAreaElement;
      if (textarea) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const selectedText = textarea.value.substring(start, end);
        const linkText = selectedText || 'enlace';
        const newText = `[${linkText}](${url})`;
        
        const newValue = textarea.value.substring(0, start) + newText + textarea.value.substring(end);
        textarea.value = newValue;
        this.appointmentNotes = newValue;
        
        textarea.focus();
      }
    }
  }

  /**
   * Insertar imagen
   */
  insertImage(): void {
    const url = prompt('Ingrese la URL de la imagen:');
    if (url) {
      const textarea = document.querySelector('.rich-editor') as HTMLTextAreaElement;
      if (textarea) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const selectedText = textarea.value.substring(start, end);
        const altText = selectedText || 'imagen';
        const newText = `![${altText}](${url})`;
        
        const newValue = textarea.value.substring(0, start) + newText + textarea.value.substring(end);
        textarea.value = newValue;
        this.appointmentNotes = newValue;
        
        textarea.focus();
      }
    }
  }

  /**
   * Insertar cita
   */
  insertBlockquote(): void {
    const textarea = document.querySelector('.rich-editor') as HTMLTextAreaElement;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const selectedText = textarea.value.substring(start, end);
      const lines = selectedText.split('\n');
      const quotedText = lines.map(line => line.trim() ? `> ${line}` : line).join('\n');
      
      const newValue = textarea.value.substring(0, start) + quotedText + textarea.value.substring(end);
      textarea.value = newValue;
      this.appointmentNotes = newValue;
      
      textarea.focus();
    }
  }

  /**
   * Aplicar formato de título
   */
  applyTitleFormat(): void {
    const textarea = document.querySelector('.rich-editor') as HTMLTextAreaElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = textarea.value.substring(start, end);
    let newText = '';

    switch (this.selectedFormat) {
      case 'h1':
        newText = `# ${selectedText}`;
        break;
      case 'h2':
        newText = `## ${selectedText}`;
        break;
      case 'h3':
        newText = `### ${selectedText}`;
        break;
      default:
        newText = selectedText;
    }

    const newValue = textarea.value.substring(0, start) + newText + textarea.value.substring(end);
    textarea.value = newValue;
    this.appointmentNotes = newValue;
    
    textarea.focus();
  }



  /**
   * Activar carga de archivos
   */
  triggerFileUpload(): void {
    const fileInput = document.querySelector('#fileInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  /**
   * Manejar selección de archivos
   */
  onFileSelected(event: any): void {
    const files = event.target.files;
    if (files && files.length > 0) {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        this.uploadFile(file);
      }
    }
    // Limpiar el input
    event.target.value = '';
  }

  /**
   * Subir archivo
   */
  uploadFile(file: File): void {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('appointmentId', this.selectedAppointment._id);

    // Simular subida de archivo (aquí deberías llamar al backend)
    const fileData = {
      name: file.name,
      size: file.size,
      type: file.type,
      uploadDate: new Date(),
      url: URL.createObjectURL(file) // Temporal, en producción sería la URL del servidor
    };

    this.appointmentFiles.push(fileData);
    console.log('Archivo subido:', fileData);
  }

  /**
   * Obtener clase de icono según el tipo de archivo
   */
  getFileIconClass(fileName: string): string {
    const extension = fileName.split('.').pop()?.toLowerCase();
    
    switch (extension) {
      case 'pdf':
        return 'fa-file-pdf';
      case 'doc':
      case 'docx':
        return 'fa-file-word';
      case 'ppt':
      case 'pptx':
        return 'fa-file-powerpoint';
      case 'jpg':
      case 'jpeg':
      case 'png':
        return 'fa-file-image';
      case 'txt':
        return 'fa-file-alt';
      default:
        return 'fa-file';
    }
  }

  /**
   * Ver archivo
   */
  viewFile(file: any): void {
    if (file.url) {
      window.open(file.url, '_blank');
    }
  }

  /**
   * Eliminar archivo
   */
  deleteFile(index: number): void {
    this.appointmentFiles.splice(index, 1);
  }

  /**
   * Guardar notas de la cita
   */
  saveAppointmentNotes(): void {
    if (!this.selectedAppointment) {
      return;
    }

    this.isSavingNotes = true;

    // Preparar archivos para enviar al backend
    const filesData = this.appointmentFiles.map(file => ({
      name: file.name,
      size: file.size,
      type: file.type,
      url: file.url,
      uploadDate: file.uploadDate
    }));

    // Obtener contenido HTML del editor Quill
    const notesHtml = this.getQuillContent();

    console.log('Guardando notas y archivos:', {
      appointmentId: this.selectedAppointment._id,
      notes: notesHtml,
      files: filesData
    });

    // Llamar al backend para actualizar notas y archivos
    this.appointmentsService.updateNotesAndFiles(
      this.selectedAppointment._id,
      notesHtml,
      filesData
    ).subscribe({
      next: (response) => {
        console.log('Respuesta del backend:', response);
        
        if (response.success) {
          // Actualizar la cita localmente
          const appointmentIndex = this.allAppointments.findIndex(apt => apt._id === this.selectedAppointment._id);
          if (appointmentIndex !== -1) {
            this.allAppointments[appointmentIndex].notes = this.appointmentNotes;
            this.allAppointments[appointmentIndex].files = this.appointmentFiles;
          }

          // Actualizar también en la lista filtrada
          const filteredIndex = this.filteredAppointments.findIndex(apt => apt._id === this.selectedAppointment._id);
          if (filteredIndex !== -1) {
            this.filteredAppointments[filteredIndex].notes = this.appointmentNotes;
            this.filteredAppointments[filteredIndex].files = this.appointmentFiles;
          }

               this.isSavingNotes = false;
               this.closeNotesModal();
               
               // Mostrar mensaje de éxito
               Swal.fire({
                 icon: 'success',
                 title: '¡Éxito!',
                 text: 'Notas y archivos guardados exitosamente',
                 confirmButtonText: 'Aceptar',
                 customClass: {
                   container: 'swal-container',
                   popup: 'swal-popup',
                   confirmButton: 'swal-confirm-button'
                 }
               });
        } else {
          this.isSavingNotes = false;
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'Error al guardar: ' + response.message,
            confirmButtonText: 'Aceptar',
            customClass: {
              container: 'swal-container',
              popup: 'swal-popup',
              confirmButton: 'swal-confirm-button'
            }
          });
        }
      },
      error: (error) => {
        console.error('Error al guardar notas:', error);
        this.isSavingNotes = false;
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Error al guardar las notas y archivos',
          confirmButtonText: 'Aceptar',
          customClass: {
            container: 'swal-container',
            popup: 'swal-popup',
            confirmButton: 'swal-confirm-button'
          }
        });
      }
    });
  }

  /**
   * Alternar vista previa
   */
  togglePreview(): void {
    this.showPreview = !this.showPreview;
  }

  /**
   * Obtener contenido de vista previa
   */
  getPreviewContent(): string {
    if (!this.appointmentNotes) return '';
    
    let content = this.appointmentNotes;
    
    // Convertir Markdown básico a HTML
    content = content
      // Títulos
      .replace(/^### (.*$)/gim, '<h3>$1</h3>')
      .replace(/^## (.*$)/gim, '<h2>$1</h2>')
      .replace(/^# (.*$)/gim, '<h1>$1</h1>')
      // Negrita
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      // Cursiva
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      // Subrayado
      .replace(/__(.*?)__/g, '<u>$1</u>')
      // Tachado
      .replace(/~~(.*?)~~/g, '<del>$1</del>')
      // Enlaces
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank">$1</a>')
      // Imágenes
      .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" style="max-width: 100%; height: auto;">')
      // Citas
      .replace(/^> (.*$)/gim, '<blockquote>$1</blockquote>')
      // Listas con viñetas
      .replace(/^• (.*$)/gim, '<li>$1</li>')
      .replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>')
      // Listas numeradas
      .replace(/^\d+\. (.*$)/gim, '<li>$1</li>')
      // Saltos de línea
      .replace(/\n/g, '<br>')
      // Comentarios de alineación
      .replace(/\[ALINEAR IZQUIERDA\]/g, '<div style="text-align: left;">')
      .replace(/\[ALINEAR CENTRO\]/g, '<div style="text-align: center;">')
      .replace(/\[ALINEAR DERECHA\]/g, '<div style="text-align: right;">')
      .replace(/\[JUSTIFICAR\]/g, '<div style="text-align: justify;">');
    
    return content;
  }

  /**
   * Limpiar formato del texto
   */
  clearFormatting(): void {
    const textarea = document.querySelector('.rich-editor') as HTMLTextAreaElement;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const selectedText = textarea.value.substring(start, end);
      
      // Remover formato Markdown
      let cleanText = selectedText
        .replace(/\*\*(.*?)\*\*/g, '$1')  // Negrita
        .replace(/\*(.*?)\*/g, '$1')      // Cursiva
        .replace(/__(.*?)__/g, '$1')      // Subrayado
        .replace(/~~(.*?)~~/g, '$1')      // Tachado
        .replace(/^#+\s*/gm, '')          // Títulos
        .replace(/^•\s*/gm, '')           // Lista con viñetas
        .replace(/^\d+\.\s*/gm, '')       // Lista numerada
        .replace(/^>\s*/gm, '')           // Citas
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')  // Enlaces
        .replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1') // Imágenes
        .replace(/\[ALINEAR [^\]]+\]/g, '')       // Comentarios de alineación
        .trim();
      
      const newValue = textarea.value.substring(0, start) + cleanText + textarea.value.substring(end);
      textarea.value = newValue;
      this.appointmentNotes = newValue;
      
      textarea.focus();
    }
  }

  /**
   * Inicializar editor Quill
   */
  initializeQuillEditor(): void {
    // Destruir instancia anterior si existe
    if (this.quillInstance) {
      this.quillInstance = null;
    }

    // Esperar a que el DOM esté listo
    setTimeout(() => {
      if (this.quillEditor && this.quillEditor.nativeElement) {
        const toolbarOptions = [
          [{ 'header': [1, 2, 3, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ 'list': 'ordered'}, { 'list': 'bullet' }],
          [{ 'align': [] }],
          ['link', 'image'],
          ['blockquote', 'code-block'],
          ['clean']
        ];

        const options = {
          theme: 'snow',
          placeholder: 'Escriba sus notas aquí...',
          modules: {
            toolbar: toolbarOptions
          },
          formats: [
            'header', 'bold', 'italic', 'underline', 'strike',
            'list', 'bullet', 'align', 'link', 'image',
            'blockquote', 'code-block'
          ]
        };

        try {
          this.quillInstance = new Quill(this.quillEditor.nativeElement, options);
          
          // Configurar dirección del texto
          this.quillInstance.root.setAttribute('dir', 'ltr');
          this.quillInstance.root.style.direction = 'ltr';
          this.quillInstance.root.style.textAlign = 'left';

          // Escuchar cambios en el editor
          this.quillInstance.on('text-change', () => {
            this.appointmentNotes = this.quillInstance?.root.innerHTML || '';
          });

          // Cargar contenido existente si hay
          if (this.appointmentNotes) {
            this.quillInstance.root.innerHTML = this.appointmentNotes;
          }

          console.log('Editor Quill inicializado correctamente');
        } catch (error) {
          console.error('Error al inicializar Quill:', error);
        }
      }
    }, 200);
  }

  /**
   * Cargar contenido en el editor Quill
   */
  loadContentToQuill(content: string): void {
    if (this.quillInstance && content) {
      // Convertir HTML a texto plano si es necesario
      const cleanContent = content.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ');
      this.quillInstance.root.innerHTML = cleanContent;
    }
  }

  /**
   * Obtener contenido del editor Quill
   */
  getQuillContent(): string {
    return this.quillInstance?.root.innerHTML || '';
  }

  /**
   * Limpiar editor Quill
   */
  clearQuillEditor(): void {
    if (this.quillInstance) {
      this.quillInstance.setText('');
    }
  }

  /**
   * Aplicar formato en Quill
   */
  applyQuillFormat(format: string, value?: any): void {
    if (this.quillInstance) {
      const range = this.quillInstance.getSelection();
      if (range) {
        this.quillInstance.format(format, value);
      }
    }
  }

  /**
   * Verificar si formato está activo en Quill
   */
  isQuillFormatActive(format: string): boolean {
    if (this.quillInstance) {
      const range = this.quillInstance.getSelection();
      if (range) {
        const formatObj = this.quillInstance.getFormat(range);
        return Boolean(formatObj[format]);
      }
    }
    return false;
  }

  /**
   * Obtener el estado del entregable "Círculo de la vida"
   * Basado en las propiedades: testNowComplete, testFutureComplete, registerQuestionsComplete
   */
  getCirculoVidaStatus(): { status: string; statusClass: string; icon: string; text: string } {
    if (!this.user || !this.user.profile) {
      return {
        status: 'pending',
        statusClass: 'pending',
        icon: 'fas fa-times',
        text: 'Pendiente'
      };
    }

    const testNow = this.user.profile.testNowComplete || false;
    const testFuture = this.user.profile.testFutureComplete || false;
    const registerQuestions = this.user.profile.registerQuestionsComplete || false;

    const completedCount = [testNow, testFuture, registerQuestions].filter(Boolean).length;

    if (completedCount === 0) {
      // Los 3 son false
      return {
        status: 'pending',
        statusClass: 'pending',
        icon: 'fas fa-times',
        text: 'Pendiente'
      };
    } else if (completedCount === 3) {
      // Los 3 son true
      return {
        status: 'completed',
        statusClass: 'completed',
        icon: 'fas fa-eye',
        text: 'Culminado'
      };
    } else {
      // 1 o 2 son true
      return {
        status: 'in-progress',
        statusClass: 'in-progress',
        icon: 'fas fa-minus',
        text: 'En curso'
      };
    }
  }

  /**
   * Abre el modal de resultados del círculo de la vida
   */
  openCircleLifeModal(): void {
    if (!this.user || (!this.user._id && !this.user.id)) {
      Swal.fire({
        icon: 'warning',
        title: 'Usuario no disponible',
        text: 'No se puede cargar los resultados del círculo de la vida'
      });
      return;
    }

    const userId = this.user._id || this.user.id;
    this.showCircleLifeModal = true;
    this.loadCircleLifeData(userId);
  }

  /**
   * Cierra el modal de resultados del círculo de la vida
   */
  closeCircleLifeModal(): void {
    this.showCircleLifeModal = false;
    this.circleLifeSeries = [];
    this.circleLifeSeriesApply = [];
    this.circleLifeCategories = [];
    this.circleLifeAnalysisAreas = [];
    // Resetear áreas de vida
    this.circleLifeAreas.forEach(area => {
      area.expanded = false;
    });
  }

  /**
   * Carga los datos del círculo de la vida del usuario
   */
  private loadCircleLifeData(userId: string): void {
    this.isLoading = true;
    this.testsService.find(userId).subscribe({
      next: (res: any) => {
        console.log('loadCircleLifeData - Respuesta completa:', res);
        console.log('loadCircleLifeData - res.data:', res.data);
        
        if (!res || !res.data) {
          console.warn('loadCircleLifeData - No hay datos en la respuesta');
          this.isLoading = false;
          Swal.fire({
            icon: 'info',
            title: 'Sin datos',
            text: 'El usuario aún no ha completado el círculo de la vida',
            confirmButtonText: 'Cerrar'
          }).then(() => {
            this.closeCircleLifeModal();
          });
          return;
        }

        // Verificar si res.data es un array
        const dataArray = Array.isArray(res.data) ? res.data : [];
        console.log('loadCircleLifeData - Total tests encontrados:', dataArray.length);
        
        // Log de la estructura de los primeros tests para debuggear
        if (dataArray.length > 0) {
          console.log('loadCircleLifeData - Estructura del primer test:', {
            test: dataArray[0],
            type: dataArray[0].type,
            typeId: dataArray[0].type?.id,
            typeIdDirect: dataArray[0].typeId,
            hasType: !!dataArray[0].type,
            typeKeys: dataArray[0].type ? Object.keys(dataArray[0].type) : []
          });
        }
        
        // Filtrar tests del círculo de la vida (tipo 2 = presente, tipo 3 = futuro)
        let tests = dataArray.filter((x: any) => {
          // Intentar diferentes formas de acceder al typeId
          const typeId = x.type?.id || x.typeId || (typeof x.type === 'number' ? x.type : null) || (x.type?._id ? parseInt(x.type._id) : null);
          const matches = typeId == 2 || typeId == 3 || typeId === '2' || typeId === '3';
          if (matches) {
            console.log('loadCircleLifeData - Test encontrado:', { 
              typeId, 
              type: x.type,
              hasQuestions: !!x.questions, 
              questionsCount: x.questions?.length 
            });
          } else if (dataArray.length > 0 && dataArray.indexOf(x) < 2) {
            // Log solo los primeros 2 para no saturar
            console.log('loadCircleLifeData - Test NO coincide:', { 
              typeId, 
              type: x.type,
              typeString: JSON.stringify(x.type).substring(0, 100)
            });
          }
          return matches;
        });
        
        console.log('loadCircleLifeData - Tests filtrados (tipo 2 o 3):', tests.length);
        
        if (tests.length === 0) {
          console.warn('loadCircleLifeData - No se encontraron tests del círculo de la vida');
          this.isLoading = false;
          Swal.fire({
            icon: 'info',
            title: 'Sin datos',
            text: 'El usuario aún no ha completado el círculo de la vida',
            confirmButtonText: 'Cerrar'
          }).then(() => {
            this.closeCircleLifeModal();
          });
          return;
        }

        tests = tests.sort((a: any, b: any) => {
          const typeIdA = a.type?.id || a.typeId;
          const typeIdB = b.type?.id || b.typeId;
          return typeIdA - typeIdB;
        });

        tests.forEach((item: any) => {
          if (item.questions && item.questions.length > 0) {
            item.questions = item.questions.slice().sort((a: any, b: any) => {
              const nameA = a.dimension?.name || a.dimension || '';
              const nameB = b.dimension?.name || b.dimension || '';
              return nameA.localeCompare(nameB);
            });
          }
        });

        // Verificar que los tests tengan preguntas con respuestas
        const validTests = tests.filter((test: any) => {
          const hasQuestions = test.questions && test.questions.length > 0;
          const hasAnswers = test.questions?.some((q: any) => q.answer && (q.answer.value || q.answer.valueNow));
          return hasQuestions && hasAnswers;
        });

        if (validTests.length === 0) {
          console.warn('loadCircleLifeData - Tests encontrados pero sin respuestas válidas');
          this.isLoading = false;
          Swal.fire({
            icon: 'info',
            title: 'Sin datos',
            text: 'El usuario aún no ha completado el círculo de la vida',
            confirmButtonText: 'Cerrar'
          }).then(() => {
            this.closeCircleLifeModal();
          });
          return;
        }

        // Obtener las dimensiones del test presente
        const presentTest = validTests.find((x: any) => {
          const typeId = x.type?.id || x.typeId;
          return typeId == 2;
        });
        
        if (!presentTest || !presentTest.questions || presentTest.questions.length === 0) {
          console.warn('loadCircleLifeData - No se encontró test presente con preguntas');
          this.isLoading = false;
          Swal.fire({
            icon: 'info',
            title: 'Sin datos',
            text: 'El usuario aún no ha completado el círculo de la vida',
            confirmButtonText: 'Cerrar'
          }).then(() => {
            this.closeCircleLifeModal();
          });
          return;
        }

        // Crear array de dimensiones con formato { name: string }
        const dimensions = presentTest.questions.map((q: any) => {
          const dimensionName = q.dimension?.name || q.dimension || '';
          return { name: dimensionName };
        });
        
        this.circleLifeCategories = dimensions.map((d: any) => d.name);

        // Crear las series con las dimensiones correctas
        this.circleLifeSeries = validTests.map((x: any) => {
          const typeId = x.type?.id || x.typeId;
          
          // Mapear los valores de las respuestas, asegurándonos de que coincidan con las dimensiones
          const data = dimensions.map((dim: any, index: number) => {
            const question = x.questions.find((q: any) => {
              const qDimensionName = q.dimension?.name || q.dimension || '';
              return qDimensionName === dim.name;
            });
            
            if (question && question.answer) {
              // Intentar obtener el valor de diferentes formas
              if (question.answer.value !== undefined && question.answer.value !== null) {
                return parseInt(question.answer.value);
              }
              if (question.answer.valueNow !== undefined && question.answer.valueNow !== null) {
                return parseInt(question.answer.valueNow);
              }
              if (question.answer.valueFuture !== undefined && question.answer.valueFuture !== null) {
                return parseInt(question.answer.valueFuture);
              }
            }
            return 0;
          });
          
          return {
            name: typeId == 2 ? 'Donde estás' : 'Donde quieres estar',
            data: data,
            dimensions: dimensions,
            hidden: false
          };
        });

        // Crear serie comparativa
        if (this.circleLifeSeries.length === 2) {
          const comparativeData = this.circleLifeSeries[0].data.map((val: number, index: number) => {
            const futureVal = this.circleLifeSeries[1].data[index];
            return futureVal - val; // Diferencia entre futuro y presente
          });
          
          this.circleLifeSeries.push({
            name: 'Comparativo',
            data: comparativeData,
            dimensions: dimensions,
            hidden: false
          });
        }
        
        // Inicializar circleLifeSeriesApply con todas las series
        // El componente radar-chart siempre espera 2 series, pero usaremos hidden para controlar qué mostrar
        if (this.circleLifeSeries.length > 0) {
          // circleLifeSeriesApply debe tener siempre las 2 primeras series para el radar-chart
          // La serie comparativa se maneja por separado
          if (this.circleLifeSeries.length >= 2) {
            this.circleLifeSeriesApply = [this.circleLifeSeries[0], this.circleLifeSeries[1]];
          } else {
            // Si solo hay 1 serie, crear una serie vacía como segunda
            this.circleLifeSeriesApply = [
              this.circleLifeSeries[0],
              {
                name: '',
                data: new Array(dimensions.length).fill(0),
                dimensions: dimensions,
                hidden: true
              }
            ];
          }
          
          // Inicializar índices
          this.circleLifeSeriesIndex = 0;
          this.circleLifeSeriesIndexComparative = this.circleLifeSeries.length > 2 ? this.circleLifeSeries.length - 1 : -1;
          
          // Inicializar mostrando solo la primera serie
          this.changeCircleLifeSeriesToShow(0);
          
          console.log('loadCircleLifeData - Series creadas:', {
            totalSeries: this.circleLifeSeries.length,
            seriesApply: this.circleLifeSeriesApply,
            dimensions: dimensions,
            categories: this.circleLifeCategories
          });
        }
        
        this.generateCircleLifeAnalysisAreas(validTests);
        this.updateCircleLifeAreasExpandedState();
        
        // Trigger update del gráfico después de un pequeño delay para asegurar que el DOM esté listo
        setTimeout(() => {
          this.circleLifeEventsSubject.next();
        }, 300);
        
        this.isLoading = false;
      },
      error: (err: any) => {
        console.error('Error al cargar datos del círculo de la vida:', err);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudieron cargar los datos del círculo de la vida'
        });
        this.isLoading = false;
        this.closeCircleLifeModal();
      }
    });
  }

  /**
   * Genera las áreas de análisis del círculo de la vida
   */
  private generateCircleLifeAnalysisAreas(tests: any[]): void {
    const presentTest = tests.find((x: any) => {
      const typeId = x.type?.id || x.typeId;
      return typeId == 2;
    });
    const futureTest = tests.find((x: any) => {
      const typeId = x.type?.id || x.typeId;
      return typeId == 3;
    });

    if (presentTest && futureTest) {
      const presentMap = new Map();
      const futureMap = new Map();

      presentTest.questions.forEach((q: any) => {
        const dimensionName = q.dimension?.name || q.dimension;
        // Intentar obtener el valor de diferentes formas
        let value = 0;
        if (q.answer?.value) value = parseInt(q.answer.value);
        else if (q.answer?.valueNow) value = parseInt(q.answer.valueNow);
        presentMap.set(dimensionName, value);
      });

      futureTest.questions.forEach((q: any) => {
        const dimensionName = q.dimension?.name || q.dimension;
        // Intentar obtener el valor de diferentes formas
        let value = 0;
        if (q.answer?.value) value = parseInt(q.answer.value);
        else if (q.answer?.valueFuture) value = parseInt(q.answer.valueFuture);
        futureMap.set(dimensionName, value);
      });

      this.circleLifeAnalysisAreas = presentTest.questions.map((q: any) => {
        const dimensionName = q.dimension?.name || q.dimension;
        const presentValue = presentMap.get(dimensionName) || 0;
        const futureValue = futureMap.get(dimensionName) || 0;
        const gap = futureValue - presentValue;

        return {
          name: (dimensionName || '').toUpperCase(),
          present: presentValue,
          future: futureValue,
          gap: gap
        };
      });

      // Calcular áreas prioritarias
      this.calculateCircleLifePriorityAndAreas();
      
      // Ordenar áreas por brecha (de mayor a menor)
      this.circleLifeAnalysisAreas.sort((a, b) => b.gap - a.gap);
    }
  }

  /**
   * Calcula las áreas prioritarias, de oportunidad y fortalezas
   */
  private calculateCircleLifePriorityAndAreas(): void {
    const registerQuestions = this.user?.profile?.registerQuestions || [];
    const priorityQuestion = registerQuestions.find((q: any) => 
      q.title?.toLowerCase().includes('De todas las áreas de tu vida, ¿cuál es la que más quieres fortalecer en este momento?'.toLowerCase())
    );
    
    this.priorityArea = priorityQuestion?.value || 'Desarrollo profesional';

    const opportunityAreasList: string[] = [];
    const strengthAreasList: string[] = [];

    this.circleLifeAnalysisAreas.forEach(area => {
      const areaName = this.formatAreaName(area.name);
      
      if (this.isOpportunityGap(area.gap)) {
        opportunityAreasList.push(areaName);
      } else if (this.isStrengthGap(area.gap)) {
        strengthAreasList.push(areaName);
      }
    });

    this.opportunityAreas = opportunityAreasList.join(', ');
    this.strengthAreas = strengthAreasList.join(', ');
  }

  /**
   * Formatea el nombre del área para mostrar
   */
  private formatAreaName(areaName: string): string {
    const areaNameMap: { [key: string]: string } = {
      'SALUD Y BIENESTAR': 'Salud y Bienestar',
      'DIVERSIÓN': 'Diversión',
      'CRECIMIENTO PERSONAL': 'Crecimiento Personal',
      'TRANSFORMACIÓN': 'Transformación',
      'CÍRCULOS SOCIALES': 'Círculos Sociales',
      'FAMILIA': 'Familia',
      'TRABAJO Y DESARROLLO PROFESIONAL': 'Trabajo y Desarrollo Profesional',
      'INGRESOS ECONÓMICOS': 'Ingresos Económicos'
    };
    return areaNameMap[areaName] || areaName;
  }

  /**
   * Determina si un gap es de oportunidad
   */
  private isOpportunityGap(gap: number): boolean {
    const gapRanges = environment.gapRanges;
    const gapTypes = environment.gapTypes;
    const matchingRange = gapRanges.find(range => gap >= range.min && gap <= range.max);
    return matchingRange ? gapTypes.opportunity.includes(matchingRange.key) : false;
  }

  /**
   * Determina si un gap es de fortaleza
   */
  private isStrengthGap(gap: number): boolean {
    const gapRanges = environment.gapRanges;
    const gapTypes = environment.gapTypes;
    const matchingRange = gapRanges.find(range => gap >= range.min && gap <= range.max);
    return matchingRange ? gapTypes.strength.includes(matchingRange.key) && gap > 0 : false;
  }

  /**
   * Obtiene la clase CSS para el gap
   */
  getGapClass(gap: number): string {
    const gapRanges = environment.gapRanges;
    const gapCssClasses = environment.gapCssClasses;
    const matchingRange = gapRanges.find(range => gap >= range.min && gap <= range.max);
    
    if (matchingRange && gapCssClasses[matchingRange.key as keyof typeof gapCssClasses]) {
      return gapCssClasses[matchingRange.key as keyof typeof gapCssClasses].small;
    }
    
    return 'gap-small';
  }

  /**
   * Obtiene la clase CSS para el gap (variante 2)
   */
  getGapClass2(gap: number): string {
    const gapRanges = environment.gapRanges;
    const gapCssClasses = environment.gapCssClasses;
    const matchingRange = gapRanges.find(range => gap >= range.min && gap <= range.max);
    
    if (matchingRange && gapCssClasses[matchingRange.key as keyof typeof gapCssClasses]) {
      return gapCssClasses[matchingRange.key as keyof typeof gapCssClasses].small2;
    }
    
    return 'gap-small2';
  }

  /**
   * Actualiza el estado de las áreas de vida basado en los gaps
   */
  private updateCircleLifeAreasExpandedState(): void {
    const dimensionToAreaMap: { [key: string]: string } = {
      'SALUD Y BIENESTAR': 'Salud y bienestar',
      'DIVERSIÓN': 'Diversión',
      'CRECIMIENTO PERSONAL': 'Crecimiento personal',
      'TRANSFORMACIÓN': 'Transformación',
      'CÍRCULOS SOCIALES': 'Círculos sociales',
      'FAMILIA': 'Familia',
      'TRABAJO Y DESARROLLO PROFESIONAL': 'Trabajo y desarrollo profesional',
      'INGRESOS ECONÓMICOS': 'Ingresos económicos'
    };

    // Actualizar cada área de vida
    this.circleLifeAreas.forEach(area => {
      const analysisArea = this.circleLifeAnalysisAreas.find(a => 
        dimensionToAreaMap[a.name] === area.name || 
        a.name.toLowerCase() === area.name.toLowerCase()
      );

      if (analysisArea) {
        const gap = analysisArea.gap;
        area.description = this.getDescriptionByGap(area.name, gap);
        area.gapClass = this.getGapClass(gap);
        area.gapClass2 = this.getGapClass2(gap);
      }
    });

    // Ordenar áreas de vida por brecha (de mayor a menor)
    this.circleLifeAreas.sort((a, b) => {
      const areaA = this.circleLifeAnalysisAreas.find(aa => 
        dimensionToAreaMap[aa.name] === a.name || 
        aa.name.toLowerCase() === a.name.toLowerCase()
      );
      const areaB = this.circleLifeAnalysisAreas.find(bb => 
        dimensionToAreaMap[bb.name] === b.name || 
        bb.name.toLowerCase() === b.name.toLowerCase()
      );
      
      const gapA = areaA?.gap || 0;
      const gapB = areaB?.gap || 0;
      
      return gapB - gapA;
    });
  }

  /**
   * Obtiene la descripción basada en el gap
   */
  private getDescriptionByGap(areaName: string, gap: number): string {
    const descriptions = environment.assessmentDescriptions;
    const gapRanges = environment.gapRanges;

    const areaDescriptions = descriptions[areaName as keyof typeof descriptions];
    if (areaDescriptions) {
      const matchingRange = gapRanges.find(range => gap >= range.min && gap <= range.max);
      
      if (matchingRange && areaDescriptions[matchingRange.key as keyof typeof areaDescriptions]) {
        return areaDescriptions[matchingRange.key as keyof typeof areaDescriptions];
      }
    }

    return 'Esta área requiere tu atención, considera revisar tus prioridades y establecer metas claras para mejorar en este aspecto de tu vida.';
  }

  /**
   * Alterna la expansión de un área de vida
   */
  toggleCircleLifeArea(index: number): void {
    this.circleLifeAreas[index].expanded = !this.circleLifeAreas[index].expanded;
  }

  /**
   * Cambia la serie a mostrar en el gráfico del círculo de la vida
   */
  changeCircleLifeSeriesToShow(index: number): void {
    // Validar índice
    if (index === this.circleLifeSeriesIndexComparative) {
      // Si es la serie comparativa, mostrar ambas series principales
      this.circleLifeSeriesIndex = index;
      this.circleLifeSeriesApply.forEach(series => series.hidden = false);
      setTimeout(() => {
        this.circleLifeEventsSubject.next();
      }, 100);
      return;
    }
    
    if (index >= this.circleLifeSeries.length) {
      return;
    }
    
    this.circleLifeSeriesIndex = index;
    
    // Ocultar todas las series primero
    this.circleLifeSeriesApply.forEach(series => series.hidden = true);
    
    // Mostrar solo la serie seleccionada (índice 0 o 1, que corresponden a las 2 primeras series)
    if (index < this.circleLifeSeriesApply.length) {
      this.circleLifeSeriesApply[index].hidden = false;
    }
    
    // Trigger update del gráfico
    setTimeout(() => {
      this.circleLifeEventsSubject.next();
    }, 100);
  }

  /**
   * Obtiene el color de una serie del círculo de la vida
   */
  getCircleLifeSeriesColor(index: number): string {
    const colors = ['#CE0024', '#FF6B35', '#4ECDC4'];
    return colors[index % colors.length] || '#CE0024';
  }

  /**
   * Obtener el estado del entregable "Análisis profundo"
   * Basado en los tests de tipo Autoconocimiento
   */
  getAnalisisProfundoStatus(): { status: string; statusClass: string; icon: string; text: string } {
    if (!this.analisisProfundoTests || this.analisisProfundoTests.length === 0) {
      return {
        status: 'pending',
        statusClass: 'pending',
        icon: 'fas fa-times',
        text: 'Pendiente'
      };
    }

    const allCompleted = this.analisisProfundoTests.every((test: any) => test.progress === 100);
    const anyInProgress = this.analisisProfundoTests.some((test: any) => test.progress > 0 && test.progress < 100);
    const allPending = this.analisisProfundoTests.every((test: any) => test.progress === 0);

    if (allCompleted) {
      return {
        status: 'completed',
        statusClass: 'completed',
        icon: 'fas fa-eye',
        text: 'Culminado'
      };
    } else if (anyInProgress || (!allPending && !allCompleted)) {
      return {
        status: 'in-progress',
        statusClass: 'in-progress',
        icon: 'fas fa-minus',
        text: 'En curso'
      };
    } else {
      return {
        status: 'pending',
        statusClass: 'pending',
        icon: 'fas fa-times',
        text: 'Pendiente'
      };
    }
  }

  /**
   * Abre el modal de análisis profundo
   */
  openAnalisisProfundoModal(): void {
    if (!this.user || (!this.user._id && !this.user.id)) {
      Swal.fire({
        icon: 'warning',
        title: 'Usuario no disponible',
        text: 'No se puede cargar los resultados del análisis profundo'
      });
      return;
    }

    const userId = this.user._id || this.user.id;
    this.showAnalisisProfundoModal = true;
    this.loadAnalisisProfundoData(userId);
  }

  /**
   * Cierra el modal de análisis profundo
   */
  closeAnalisisProfundoModal(): void {
    this.showAnalisisProfundoModal = false;
    // No limpiar analisisProfundoTests porque se necesita para mostrar el estado
  }

  /**
   * Carga los datos del análisis profundo del usuario
   */
  private loadAnalisisProfundoData(userId: string): void {
    // Si ya tenemos los datos cargados, no recargar
    if (this.analisisProfundoTests && this.analisisProfundoTests.length > 0) {
      this.analisisProfundoLoading = false;
      return;
    }

    this.analisisProfundoLoading = true;
    this.testsService.find(userId, TEST_TYPES.Autoconocimiento.id).subscribe({
      next: (res: any) => {
        if (res.data) {
          this.analisisProfundoTests = this.initializeAnalisisProfundoTests(res.data);
          this.updateAnalisisProfundoProgress();
        } else {
          this.analisisProfundoTests = [];
        }
        this.analisisProfundoLoading = false;
      },
      error: (err: any) => {
        console.error('Error al cargar datos del análisis profundo:', err);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudieron cargar los datos del análisis profundo'
        });
        this.analisisProfundoLoading = false;
        this.closeAnalisisProfundoModal();
      }
    });
  }

  /**
   * Inicializa los tests eliminando duplicados
   */
  private initializeAnalisisProfundoTests(tests: any[]): any[] {
    const uniqueTests = new Map<string, any>();
    
    tests.forEach((test: any) => {
      const key = test._id || test.id || test.dimensions?.[0]?.area?.name;
      if (key && !uniqueTests.has(key)) {
        uniqueTests.set(key, test);
      }
    });
    
    return Array.from(uniqueTests.values());
  }

  /**
   * Actualiza el progreso de los tests de análisis profundo
   */
  private updateAnalisisProfundoProgress(): void {
    this.analisisProfundoTests.forEach((test: any) => {
      let progress = 0;
      let total = 0;
      
      if (test.dimensions) {
        test.dimensions.forEach((dimension: any) => {
          if (dimension.questions) {
            dimension.questions.forEach((question: any) => {
              total++;
              if (this.isAnalisisProfundoQuestionAnswered(question)) {
                progress++;
              }
            });
          }
        });
      }
      
      test.progress = total > 0 ? Math.round((progress / total) * 100) : 0;
    });
  }

  /**
   * Verifica si una pregunta del análisis profundo está respondida
   */
  private isAnalisisProfundoQuestionAnswered(question: any): boolean {
    return !!(question.answer && 
           question.answer.valueNow >= 1 && question.answer.valueNow <= 10 && 
           question.answer.valueFuture >= 1 && question.answer.valueFuture <= 10);
  }

  /**
   * Calcula la brecha de una pregunta
   */
  calculateAnalisisProfundoGap(question: any): number {
    const valueNow = question.answer?.valueNow || 0;
    const valueFuture = question.answer?.valueFuture || 0;
    return valueFuture - valueNow;
  }

  /**
   * Obtiene la clase CSS para el gap del análisis profundo
   */
  getAnalisisProfundoGapClass(gap: number): string {
    if (gap <= 3) return 'gap-low';
    if (gap <= 6) return 'gap-medium';
    return 'gap-high';
  }

  /**
   * Calcula el promedio del análisis presente
   */
  getAnalisisProfundoAveragePresent(questions: any[]): number {
    if (!questions || questions.length === 0) return 0;
    const sum = questions.reduce((acc, q) => acc + (q.answer?.valueNow || 0), 0);
    return Math.round(sum / questions.length);
  }

  /**
   * Calcula el promedio del análisis futuro
   */
  getAnalisisProfundoAverageFuture(questions: any[]): number {
    if (!questions || questions.length === 0) return 0;
    const sum = questions.reduce((acc, q) => acc + (q.answer?.valueFuture || 0), 0);
    return Math.round(sum / questions.length);
  }

  /**
   * Calcula el promedio de la brecha
   */
  getAnalisisProfundoAverageGap(questions: any[]): number {
    if (!questions || questions.length === 0) return 0;
    const sum = questions.reduce((acc, q) => {
      const gap = (q.answer?.valueFuture || 0) - (q.answer?.valueNow || 0);
      return acc + gap;
    }, 0);
    return Math.round(sum / questions.length);
  }

  /**
   * Obtiene el número del test formateado
   */
  getAnalisisProfundoTestNumber(test: any, index: number): string {
    return String(index + 1).padStart(2, '0');
  }

  // ========== PROPIEDADES PARA MODAL DE MAPA DE IDENTIDAD (PRD) ==========
  
  showIdentityMapModal: boolean = false;
  identityMapPRDTests: any[] = [];
  identityMapPRDLoading: boolean = false;
  identityMapPRDCompleted: boolean = false;
  
  // Propiedades para la Guía de Reflexión Personal
  identityMapReflectionSections: any[] = [];
  identityMapReflectionAnswers: any[] = [];
  identityMapReflectionGuideCompleted: boolean = false;
  identityMapReflectionCompletedAt: string | null = null;
  identityMapReflectionGuideExpanded: boolean = false;

  // ========== MÉTODOS PARA MAPA DE IDENTIDAD ==========

  /**
   * Obtener el estado del entregable "Mapa de Identidad"
   * Basado en step2Complete o si las secciones están completadas
   */
  getIdentityMapStatus(): { status: string; statusClass: string; icon: string; text: string } {
    const step1Complete = this.user?.stepsAnswers?.step1?.step1Complete ? true : false;
    
    return {
      status: step1Complete ? 'completed' : 'pending',
      statusClass: step1Complete ? 'completed' : 'pending',
      icon: step1Complete ? 'fas fa-eye' : 'fas fa-times',
      text: step1Complete ? 'Culminado' : 'Pendiente'
    };
  }

  /**
   * Abre el modal de Mapa de Identidad (PRD)
   */
  openIdentityMapModal(): void {
    if (!this.user || (!this.user._id && !this.user.id)) {
      Swal.fire({
        icon: 'warning',
        title: 'Usuario no disponible',
        text: 'No se puede cargar los resultados del mapa de identidad'
      });
      return;
    }

    const userId = this.user._id || this.user.id;
    this.showIdentityMapModal = true;
    this.loadIdentityMapPRDData(userId);
    this.loadIdentityMapReflectionGuideData(userId);
  }

  /**
   * Cierra el modal de Mapa de Identidad (PRD)
   */
  closeIdentityMapModal(): void {
    this.showIdentityMapModal = false;
    // No limpiar identityMapPRDTests porque se necesita para mostrar el estado
  }

  /**
   * Carga los datos PRD para el modal de Mapa de Identidad
   */
  private loadIdentityMapPRDData(userId: string): void {
    // Si ya tenemos los datos cargados, no recargar
    if (this.identityMapPRDTests && this.identityMapPRDTests.length > 0) {
      this.identityMapPRDLoading = false;
      return;
    }

    this.identityMapPRDLoading = true;
    this.testsService.find(userId, TEST_TYPES.Autoconocimiento.id).subscribe({
      next: (res: any) => {
        if (res.data) {
          this.identityMapPRDTests = this.initializeIdentityMapPRDTests(res.data);
          this.identityMapPRDCompleted = this.areAllDimensionsCompletedForPRD();
        } else {
          this.identityMapPRDTests = [];
          this.identityMapPRDCompleted = false;
        }
        this.identityMapPRDLoading = false;
      },
      error: (err: any) => {
        console.error('Error al cargar datos PRD del mapa de identidad:', err);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudieron cargar los datos del mapa de identidad'
        });
        this.identityMapPRDLoading = false;
        this.closeIdentityMapModal();
      }
    });
  }

  /**
   * Inicializa los tests PRD eliminando duplicados
   */
  private initializeIdentityMapPRDTests(tests: any[]): any[] {
    const uniqueTests = new Map<string, any>();
    
    tests.forEach(test => {
      const key = test._id || test.id || test.dimensions[0]?.area?.name;
      if (key && !uniqueTests.has(key)) {
        uniqueTests.set(key, test);
      }
    });
    
    return Array.from(uniqueTests.values());
  }

  /**
   * Verifica si todas las dimensiones están completadas para PRD
   */
  private areAllDimensionsCompletedForPRD(): boolean {
    if (!this.identityMapPRDTests || this.identityMapPRDTests.length === 0) {
      return false;
    }
    
    return this.identityMapPRDTests.every((test: any) => {
      if (!test.dimensions || test.dimensions.length === 0) {
        return false;
      }
      return test.dimensions.every((dimension: any) => {
        if (!dimension.questions || dimension.questions.length === 0) {
          return false;
        }
        return dimension.questions.every((question: any) => {
          return question.answer && 
                 question.answer.valueNow >= 1 && 
                 question.answer.valueNow <= 10 && 
                 question.answer.valueFuture >= 1 && 
                 question.answer.valueFuture <= 10;
        });
      });
    });
  }

  /**
   * Obtiene el promedio de una dimensión
   */
  getIdentityMapPRDAverage(dimension: any, type: string): number {
    if (!dimension.questions || dimension.questions.length === 0) {
      return 0;
    }
    
    let total = 0;
    if (type === "now") {
      dimension.questions.forEach((question: any) => {
        total += question.answer?.valueNow || 0;
      });
    } else {
      dimension.questions.forEach((question: any) => {
        total += question.answer?.valueFuture || 0;
      });
    }
    return total / dimension.questions.length;
  }

  /**
   * Carga los datos de la guía de reflexión personal
   */
  private loadIdentityMapReflectionGuideData(userId: string): void {
    this.userService.getReflectionGuideAnswers(userId).subscribe({
      next: (res: any) => {
        const data = res?.data;
        if (data) {
          const hasAnswers = data.answers && Array.isArray(data.answers) && data.answers.length > 0;
          const hasSections = data.sections && Array.isArray(data.sections) && data.sections.length > 0;
          
          this.identityMapReflectionGuideCompleted = hasAnswers && hasSections;
          this.identityMapReflectionAnswers = data.answers || [];
          this.identityMapReflectionSections = (data.sections && data.sections.length > 0) ? data.sections : this.initializeIdentityMapReflectionSections();
          this.identityMapReflectionCompletedAt = data.completedAt || null;
        } else {
          this.identityMapReflectionSections = this.initializeIdentityMapReflectionSections();
        }
      },
      error: (err: any) => {
        console.warn('No se pudo obtener el estado de la guía de reflexión:', err);
        this.identityMapReflectionSections = this.initializeIdentityMapReflectionSections();
      }
    });
  }

  /**
   * Inicializa las secciones de reflexión personal
   */
  private initializeIdentityMapReflectionSections(): any[] {
    return [
      {
        id: 'section-1',
        title: 'Sección 1. Observa tus resultados',
        questions: [
          {
            id: 'q1-1',
            text: '¿Qué notas al comparar tu Círculo de Vida actual con el que proyectaste a futuro?',
            type: 'text',
            required: true
          },
          {
            id: 'q1-2',
            text: '¿Qué diferencias o brechas son más evidentes en cada dimensión (calidad de vida, desarrollo personal y profesional, relaciones personales, recursos para el futuro) y dentro de cada una de ellas (subdimensiones)?',
            type: 'dimension-pair',
            required: true
          }
        ]
      },
      {
        id: 'section-2',
        title: 'Sección 2. Conecta con lo que realmente es importante para ti',
        questions: [
          {
            id: 'q2-1',
            text: '¿Qué emociones se activan al observar tu situación actual en cada dimensión y subdimensión?',
            type: 'dimension-pair',
            required: true
          },
          {
            id: 'q2-2',
            text: '¿Qué emociones surgen al imaginarte logrando tu visión futura?',
            type: 'text',
            required: true
          }
        ]
      },
      {
        id: 'section-3',
        title: 'Sección 3. Reconoce patrones o relaciones',
        questions: [
          {
            id: 'q3-1',
            text: '¿Observas algún patrón común entre las dimensiones o subdimensiones? (por ejemplo, ¿cómo impacta tu calidad de vida en tu desarrollo profesional o en tus relaciones?)',
            type: 'dimension-pair',
            required: true
          },
          {
            id: 'q3-2',
            text: '¿Hay alguna dimensión o subdimensión que parezca estar influyendo fuertemente en las demás?',
            type: 'dimension-pair',
            required: true
          }
        ]
      },
      {
        id: 'section-4',
        title: 'Sección 4. Reflexiona sobre tu trayectoria',
        questions: [
          {
            id: 'q4-1',
            text: '¿Qué decisiones, hábitos o circunstancias crees que han contribuido a tu situación actual en cada una de estas dimensiones y subdimensiones?',
            type: 'dimension-pair',
            required: true
          },
          {
            id: 'q4-2',
            text: '¿Qué momentos clave de tu vida han influido en cómo vives hoy cada una de ellas?',
            type: 'text',
            required: true
          }
        ]
      },
      {
        id: 'section-5',
        title: 'Sección 5. Abre posibilidades',
        questions: [
          {
            id: 'q5-1',
            text: 'Si miras tus resultados con ojos de oportunidad, ¿qué ves posible para ti en cada dimensión y subdimensiones?',
            type: 'dimension-pair',
            required: true
          },
          {
            id: 'q5-2',
            text: '¿Qué nuevas preguntas te surgen sobre tu futuro al reflexionar en estas áreas?',
            type: 'text',
            required: true
          }
        ]
      },
      {
        id: 'section-6',
        title: 'Sección 6. Cierre de la Reflexión',
        questions: [
          {
            id: 'q6-1',
            text: '¿Qué aprendizajes o comprensiones principales te llevas de esta exploración?',
            type: 'text',
            required: true
          }
        ]
      }
    ];
  }

  /**
   * Obtiene la respuesta de una pregunta específica
   */
  getIdentityMapReflectionAnswer(questionId: string): any {
    return this.identityMapReflectionAnswers.find(answer => answer.questionId === questionId);
  }

  /**
   * Obtiene los pares de dimensión para una pregunta específica
   */
  getIdentityMapReflectionDimensionPairs(questionId: string): any[] {
    const answer = this.getIdentityMapReflectionAnswer(questionId);
    if (answer && Array.isArray(answer.answer)) {
      return answer.answer;
    }
    return [];
  }

  /**
   * Alterna la visualización de la guía de reflexión personal
   */
  toggleIdentityMapReflectionGuide(): void {
    this.identityMapReflectionGuideExpanded = !this.identityMapReflectionGuideExpanded;
  }

  /**
   * Verifica si se debe mostrar la guía de reflexión personal
   */
  shouldShowIdentityMapReflectionGuide(): boolean {
    return this.identityMapPRDCompleted && this.identityMapPRDTests.length > 0;
  }

  // ========== PROPIEDADES PARA MODAL DE MAPA DE IDENTIDAD (IDENTITY MAP) ==========
  
  showIdentityMapDetailsModal: boolean = false;
  identityMapData: any = null;
  identityMapLoading: boolean = false;

  // ========== PROPIEDADES PARA MODAL DE VISIONBOARD ==========
  
  showVisionboardModal: boolean = false;
  userVisionboards: Visionboard[] = [];
  visionboardImages: VisionboardImage[] = [];
  visionboardLoading: boolean = false;
  visionboardTitle: string = '';
  visionboardReflectionQuestions: string[] = [];
  visionboardReflectionAnswers: string[] = [];
  visionboardReflectionCompleted: boolean = false;
  
  // Propiedades para el canvas del whiteboard (solo lectura)
  @ViewChild('visionboardWhiteboardCanvas') visionboardWhiteboardCanvas!: ElementRef<HTMLCanvasElement>;
  visionboardCanvas: HTMLCanvasElement | null = null;
  visionboardCtx: CanvasRenderingContext2D | null = null;
  visionboardTextElements: Array<{
    id: string;
    text: string;
    x: number;
    y: number;
    fontSize: number;
    color: string;
    fontFamily: string;
  }> = [];

  // Propiedades para PRD (Revisión)
  showPRDModal: boolean = false;
  prdTests: any[] = [];
  prdHighPriorityQuestions: any[] = [];
  prdLoading: boolean = false;
  prdRankingCompleted: boolean = false;

  // ========== MÉTODOS PARA MAPA DE IDENTIDAD (IDENTITY MAP) ==========

  /**
   * Obtener el estado del entregable "Mapa de Identidad"
   * Basado en las secciones: LineadeVida, MiPasado, MiPresente
   */
  getIdentityMapDetailsStatus(): { status: string; statusClass: string; icon: string; text: string } {
    const step2 = this.user?.stepsAnswers?.step2;
    
    // Verificar si todas las secciones están completadas
    const lineadeVidaCompleted = step2?.LineadeVida?.completedAt ? true : false;
    const miPasadoCompleted = step2?.MiPasado?.completedAt ? true : false;
    const miPresenteCompleted = step2?.MiPresente?.completedAt ? true : false;

    // Si todas las secciones están completadas, está completado
    if (lineadeVidaCompleted && miPasadoCompleted && miPresenteCompleted) {
      return {
        status: 'completed',
        statusClass: 'completed',
        icon: 'fas fa-eye',
        text: 'Culminado'
      };
    }

    // Verificar si al menos una sección tiene datos iniciados
    const hasLifeLineData = step2?.LineadeVida?.peaks?.length > 0 || step2?.LineadeVida?.valleys?.length > 0;
    const hasPastData = step2?.MiPasado?.questions?.length > 0 && 
                       step2.MiPasado.questions.some((q: any) => q.answer && q.answer.trim());
    const hasPresentData = step2?.MiPresente?.presentQuadrants?.length > 0;

    // Si al menos una sección está iniciada o completada, está en progreso
    if (lineadeVidaCompleted || miPasadoCompleted || miPresenteCompleted || hasLifeLineData || hasPastData || hasPresentData) {
      return {
        status: 'in-progress',
        statusClass: 'in-progress',
        icon: 'fas fa-minus',
        text: 'En curso'
      };
    }

    // Por defecto, está pendiente
    return {
      status: 'pending',
      statusClass: 'pending',
      icon: 'fas fa-times',
      text: 'Pendiente'
    };
  }

  /**
   * Abre el modal de Mapa de Identidad (Identity Map)
   */
  openIdentityMapDetailsModal(): void {
    if (!this.user || (!this.user._id && !this.user.id)) {
      Swal.fire({
        icon: 'warning',
        title: 'Usuario no disponible',
        text: 'No se puede cargar los resultados del mapa de identidad'
      });
      return;
    }

    const userId = this.user._id || this.user.id;
    this.showIdentityMapDetailsModal = true;
    this.loadIdentityMapDetailsData(userId);
  }

  /**
   * Cierra el modal de Mapa de Identidad
   */
  closeIdentityMapDetailsModal(): void {
    this.showIdentityMapDetailsModal = false;
  }

  /**
   * Verifica si un cuadrante está vacío
   */
  isQuadrantEmpty(index: number): boolean {
    if (!this.identityMapData?.present?.presentQuadrants || 
        !this.identityMapData.present.presentQuadrants[index] ||
        !this.identityMapData.present.presentQuadrants[index].items) {
      return true;
    }
    const items = this.identityMapData.present.presentQuadrants[index].items;
    return items.every((i: string) => !i || !i.trim());
  }

  /**
   * Obtiene el estado del Visionboard
   */
  getVisionboardStatus(): { status: string; statusClass: string; icon: string; text: string } {
    const visionBoardComplete = this.user?.stepsAnswers?.step3?.VisionBoardComplete === true;
    
    if (visionBoardComplete) {
      return {
        status: 'completed',
        statusClass: 'completed',
        icon: 'fas fa-eye',
        text: 'Culminado'
      };
    }

    // Verificar si hay visionboards (del usuario o cargados)
    const hasVisionboards = (this.user?.visionboards && this.user.visionboards.length > 0) || 
                            (this.userVisionboards && this.userVisionboards.length > 0);
    if (hasVisionboards) {
      return {
        status: 'in-progress',
        statusClass: 'in-progress',
        icon: 'fas fa-minus',
        text: 'En curso'
      };
    }

    return {
      status: 'pending',
      statusClass: 'pending',
      icon: 'fas fa-times',
      text: 'Pendiente'
    };
  }

  /**
   * Abre el modal de Visionboard
   */
  openVisionboardModal(): void {
    if (!this.user || (!this.user._id && !this.user.id)) {
      Swal.fire({
        icon: 'warning',
        title: 'Usuario no disponible',
        text: 'No se puede cargar los resultados del visionboard'
      });
      return;
    }

    const userId = this.user._id || this.user.id;
    this.showVisionboardModal = true;
    this.loadVisionboardData(userId);
  }

  /**
   * Cierra el modal de Visionboard
   */
  closeVisionboardModal(): void {
    this.showVisionboardModal = false;
    // Limpiar el canvas
    this.visionboardCanvas = null;
    this.visionboardCtx = null;
    this.visionboardTextElements = [];
  }

  /**
   * Obtiene la preview de una imagen por su ID
   */
  getVisionboardPreview(imageId: string | undefined): string | null {
    if (!imageId) return null;
    const image = this.visionboardImages.find(img => img.id === imageId);
    return image ? image.imageData : null;
  }

  /**
   * Carga los datos del visionboard
   */
  private loadVisionboardData(userId: string): void {
    this.visionboardLoading = true;
    
    // Cargar visionboards
    this.visionboardService.getUserVisionboards(userId).subscribe({
      next: (response) => {
        this.userVisionboards = response.data || [];
        
        // Inicializar el título del vision board
        if (this.userVisionboards.length > 0 && this.userVisionboards[0].name) {
          this.visionboardTitle = this.userVisionboards[0].name;
        }

        // Cargar las imágenes también
        this.authService.findUserById(userId).subscribe({
          next: (userResponse) => {
            if (userResponse.data) {
              // Cargar imágenes del visionboard
              if (userResponse.data.visionboardImages) {
                this.visionboardImages = userResponse.data.visionboardImages;
              }

              // Cargar preguntas de reflexión
              if (userResponse.data.stepsAnswers?.step3?.VisionBoardReflection) {
                const reflection = userResponse.data.stepsAnswers.step3.VisionBoardReflection;
                if (reflection.questions && Array.isArray(reflection.questions)) {
                  this.visionboardReflectionQuestions = reflection.questions;
                }
                if (reflection.answers && Array.isArray(reflection.answers)) {
                  this.visionboardReflectionAnswers = reflection.answers;
                }
                this.visionboardReflectionCompleted = !!reflection.completedAt;
              }
            }
            this.visionboardLoading = false;
            
            // Inicializar y cargar el canvas del whiteboard después de cargar los datos
            setTimeout(() => {
              this.initializeVisionboardCanvas();
            }, 100);
          },
          error: (err: any) => {
            console.error('Error cargando imágenes del usuario:', err);
            this.visionboardLoading = false;
          }
        });
      },
      error: (err: any) => {
        console.error('Error cargando visionboards del usuario:', err);
        this.visionboardLoading = false;
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudieron cargar los datos del visionboard'
        });
        this.closeVisionboardModal();
      }
    });
  }

  /**
   * Inicializa el canvas del whiteboard en modo solo lectura
   */
  private initializeVisionboardCanvas(): void {
    if (!this.visionboardWhiteboardCanvas || !this.userVisionboards || this.userVisionboards.length === 0) {
      return;
    }

    this.visionboardCanvas = this.visionboardWhiteboardCanvas.nativeElement;
    this.visionboardCtx = this.visionboardCanvas.getContext('2d');
    
    if (!this.visionboardCtx) {
      return;
    }

    // Obtener las dimensiones del grid masonry
    const masonryGrid = document.querySelector('.visionboard-display .vb-masonry-grid') as HTMLElement;
    if (masonryGrid) {
      const rect = masonryGrid.getBoundingClientRect();
      this.visionboardCanvas.width = rect.width;
      this.visionboardCanvas.height = rect.height;
      this.visionboardCanvas.style.width = rect.width + 'px';
      this.visionboardCanvas.style.height = rect.height + 'px';
    }

    // Cargar los datos del whiteboard
    this.loadVisionboardWhiteboardData();
  }

  /**
   * Carga los datos del whiteboard (dibujos y textos)
   */
  private loadVisionboardWhiteboardData(): void {
    if (!this.visionboardCanvas || !this.visionboardCtx || this.userVisionboards.length === 0) {
      return;
    }

    const currentVisionboard = this.userVisionboards[0];

    // Cargar elementos de texto si existen
    if (currentVisionboard.textElements) {
      try {
        this.visionboardTextElements = JSON.parse(currentVisionboard.textElements);
      } catch (error) {
        console.error('Error cargando elementos de texto:', error);
        this.visionboardTextElements = [];
      }
    }

    // Cargar imagen de la pizarra si existe
    if (currentVisionboard.whiteboardData) {
      try {
        const img = new Image();
        img.onload = () => {
          // Verificar si la imagen es solo un fondo blanco
          const tempCanvas = document.createElement('canvas');
          const tempCtx = tempCanvas.getContext('2d')!;
          tempCanvas.width = img.width;
          tempCanvas.height = img.height;
          tempCtx.drawImage(img, 0, 0);
          
          const imageData = tempCtx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
          const pixels = imageData.data;
          
          let isWhiteBackground = true;
          for (let i = 0; i < pixels.length; i += 4) {
            const r = pixels[i];
            const g = pixels[i + 1];
            const b = pixels[i + 2];
            const a = pixels[i + 3];
            
            if (a > 0 && (r < 250 || g < 250 || b < 250)) {
              isWhiteBackground = false;
              break;
            }
          }
          
          if (!isWhiteBackground && this.visionboardCtx && this.visionboardCanvas) {
            this.visionboardCtx.clearRect(0, 0, this.visionboardCanvas.width, this.visionboardCanvas.height);
            this.visionboardCtx.drawImage(img, 0, 0, this.visionboardCanvas.width, this.visionboardCanvas.height);
          }
          
          // Dibujar elementos de texto
          this.drawVisionboardTextElements();
        };
        img.onerror = (error) => {
          console.error('Error cargando imagen de la pizarra:', error);
          // Dibujar solo los textos si la imagen falla
          this.drawVisionboardTextElements();
        };
        img.src = currentVisionboard.whiteboardData;
      } catch (error) {
        console.error('Error procesando datos de la pizarra:', error);
        // Dibujar solo los textos si hay error
        this.drawVisionboardTextElements();
      }
    } else {
      // Si no hay imagen, solo dibujar los textos
      this.drawVisionboardTextElements();
    }
  }

  /**
   * Dibuja los elementos de texto en el canvas
   */
  private drawVisionboardTextElements(): void {
    if (!this.visionboardCtx || this.visionboardTextElements.length === 0) {
      return;
    }

    this.visionboardTextElements.forEach(textElement => {
      this.visionboardCtx!.font = `${textElement.fontSize}px ${textElement.fontFamily}`;
      this.visionboardCtx!.fillStyle = textElement.color;
      this.visionboardCtx!.fillText(textElement.text, textElement.x, textElement.y);
    });
  }

  /**
   * Carga los datos del mapa de identidad
   */
  private loadIdentityMapDetailsData(userId: string): void {
    this.identityMapLoading = true;
    this.userService.getIdentityMapData(userId).subscribe({
      next: (res: any) => {
        const data = res?.data;
        if (data) {
          this.identityMapData = {
            lifeLine: {
              peaks: data.LineadeVida?.peaks || [],
              valleys: data.LineadeVida?.valleys || [],
              completedAt: data.LineadeVida?.completedAt || null
            },
            past: {
              questions: (data.MiPasado?.questions && data.MiPasado.questions.length > 0) 
                ? data.MiPasado.questions 
                : [
                    { id: 'past-1', text: 'De tus picos, ¿cuál es el hito que más orgullo te genera y por qué?', answer: '' },
                    { id: 'past-2', text: 'De tus valles, ¿qué experiencia se convirtió en el mayor aprendizaje?', answer: '' },
                    { id: 'past-3', text: '¿Qué valores o capacidades se repiten en esas situaciones?', answer: '' },
                    { id: 'past-4', text: '¿Identificas un patrón o algo que se repita en tus experiencias?', answer: '' },
                    { id: 'past-5', text: 'Al volver a mirar esos momentos que marcaron tu historia, tanto los de orgullo como los de aprendizaje, ¿qué emociones despiertan en ti hoy? Piensa si esos recuerdos han transformado la forma en la que te percibes, en cómo tomas decisiones o en el rumbo que eliges para tu vida. ¿Qué significado cobran ahora, desde tu presente, y qué huellas reconoces que aún siguen guiando tu camino?', answer: '' }
                  ],
              completedAt: data.MiPasado?.completedAt || null
            },
            present: {
              presentQuadrants: (data.MiPresente?.presentQuadrants && data.MiPresente.presentQuadrants.length > 0)
                ? data.MiPresente.presentQuadrants
                : [
                    { items: ['', '', ''] }, // Fortaleza
                    { items: ['', '', ''] }, // Riesgo
                    { items: ['', '', ''] }, // Aprendizaje
                    { items: ['', '', ''] }  // Evitar
                  ],
              stopDoing: data.MiPresente?.stopDoing || '',
              doubleDown: data.MiPresente?.doubleDown || '',
              objectives: data.MiPresente?.objectives || '',
              completedAt: data.MiPresente?.completedAt || null
            }
          };
        } else {
          this.identityMapData = {
            lifeLine: { peaks: [], valleys: [], completedAt: null },
            past: {
              questions: [
                { id: 'past-1', text: 'De tus picos, ¿cuál es el hito que más orgullo te genera y por qué?', answer: '' },
                { id: 'past-2', text: 'De tus valles, ¿qué experiencia se convirtió en el mayor aprendizaje?', answer: '' },
                { id: 'past-3', text: '¿Qué valores o capacidades se repiten en esas situaciones?', answer: '' },
                { id: 'past-4', text: '¿Identificas un patrón o algo que se repita en tus experiencias?', answer: '' },
                { id: 'past-5', text: 'Al volver a mirar esos momentos que marcaron tu historia, tanto los de orgullo como los de aprendizaje, ¿qué emociones despiertan en ti hoy? Piensa si esos recuerdos han transformado la forma en la que te percibes, en cómo tomas decisiones o en el rumbo que eliges para tu vida. ¿Qué significado cobran ahora, desde tu presente, y qué huellas reconoces que aún siguen guiando tu camino?', answer: '' }
              ],
              completedAt: null
            },
            present: {
              presentQuadrants: [
                { items: ['', '', ''] },
                { items: ['', '', ''] },
                { items: ['', '', ''] },
                { items: ['', '', ''] }
              ],
              stopDoing: '',
              doubleDown: '',
              objectives: '',
              completedAt: null
            }
          };
        }
        this.identityMapLoading = false;
      },
      error: (err: any) => {
        console.error('Error al cargar datos del mapa de identidad:', err);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudieron cargar los datos del mapa de identidad'
        });
        this.identityMapLoading = false;
        this.closeIdentityMapDetailsModal();
      }
    });
  }

  // ========== MÉTODOS PARA PRD (REVISIÓN) ==========

  /**
   * Obtener el estado del entregable "PRD"
   */
  getPRDStatus(): { statusClass: string; icon: string; text: string } {
    if (!this.user) {
      return { statusClass: 'pending', icon: 'fas fa-times', text: 'Pendiente' };
    }

    // El estado se determina por phoenixSessions6Completed en step5
    const phoenixSessions6Completed = this.user?.stepsAnswers?.step5?.phoenixSessions6Completed;

    if (phoenixSessions6Completed) {
      return { statusClass: 'completed', icon: 'fas fa-check-circle', text: 'Culminado' };
    }

    // Si hay datos de priorización pero no está completado, está en progreso
    if (this.prdHighPriorityQuestions.length > 0 || this.prdTests.length > 0) {
      return { statusClass: 'in-progress', icon: 'fas fa-clock', text: 'En curso' };
    }

    return { statusClass: 'pending', icon: 'fas fa-times', text: 'Pendiente' };
  }

  /**
   * Abre el modal de PRD
   */
  openPRDModal(): void {
    if (!this.user) {
      Swal.fire({
        icon: 'warning',
        title: 'Advertencia',
        text: 'No hay información del usuario disponible'
      });
      return;
    }

    this.showPRDModal = true;
    this.loadPRDData(this.user._id || this.user.id);
  }

  /**
   * Cierra el modal de PRD
   */
  closePRDModal(): void {
    this.showPRDModal = false;
  }

  /**
   * Carga los datos de PRD (tests y prioridades)
   */
  loadPRDData(userId: string): void {
    // Limpiar datos previos para asegurar carga fresca
    this.prdTests = [];
    this.prdHighPriorityQuestions = [];

    this.prdLoading = true;

    // También verificar el estado desde el usuario primero
    const phoenixSessions6Completed = this.user?.stepsAnswers?.step5?.phoenixSessions6Completed;
    if (phoenixSessions6Completed) {
      this.prdRankingCompleted = true;
    }

    // Cargar tests de Autoconocimiento
    this.testsService.find(userId, TEST_TYPES.Autoconocimiento.id).subscribe({
      next: (res: any) => {
        if (res.data && res.data.length > 0) {
          this.prdTests = this.initializePRDTests(res.data);
          
          // Cargar prioridades guardadas
          this.userService.getPriorizationData(userId).subscribe({
            next: (priorizationRes: any) => {
              const data = priorizationRes?.data;
              if (data) {
                this.prdRankingCompleted = data.rankingCompleted || false;
                
                if (data.prioridades && data.prioridades.length > 0) {
                  this.restorePRDPriorities(data.prioridades);
                }
              }
              
              // Obtener preguntas de alta prioridad después de restaurar prioridades
              this.prdHighPriorityQuestions = this.getPRDHighPriorityQuestions();
              console.log('PRD High Priority Questions:', this.prdHighPriorityQuestions);
              this.prdLoading = false;
            },
            error: (err: any) => {
              console.error('Error al cargar prioridades:', err);
              // Obtener preguntas de alta prioridad aunque no haya prioridades guardadas
              this.prdHighPriorityQuestions = this.getPRDHighPriorityQuestions();
              console.log('PRD High Priority Questions (sin prioridades):', this.prdHighPriorityQuestions);
              this.prdLoading = false;
            }
          });
        } else {
          console.log('No se encontraron tests de Autoconocimiento');
          this.prdLoading = false;
        }
      },
      error: (err: any) => {
        console.error('Error al cargar tests de PRD:', err);
        this.prdLoading = false;
      }
    });
  }

  /**
   * Inicializa los tests de PRD eliminando duplicados
   */
  private initializePRDTests(tests: any[]): any[] {
    const uniqueTests = new Map<string, any>();
    
    tests.forEach(test => {
      const key = test._id || test.id || test.dimensions[0]?.area?.name;
      if (key && !uniqueTests.has(key)) {
        uniqueTests.set(key, test);
      }
    });
    
    return Array.from(uniqueTests.values());
  }

  /**
   * Restaura las prioridades en las preguntas correspondientes
   */
  private restorePRDPriorities(savedPriorities: any[]): void {
    if (!this.prdTests || this.prdTests.length === 0) return;
    if (!savedPriorities || savedPriorities.length === 0) return;

    savedPriorities.forEach((savedPriority) => {
      this.prdTests.forEach(test => {
        test.dimensions.forEach((dimension: any) => {
          dimension.questions.forEach((question: any) => {
            if (question.name === savedPriority.questionName && 
                dimension.name === savedPriority.dimensionName) {
              question.priority = savedPriority.priority;
              if (savedPriority.ranking) {
                question.ranking = savedPriority.ranking;
              }
            }
          });
        });
      });
    });
  }

  /**
   * Calcula la brecha de una pregunta
   */
  calculatePRDGap(question: any): number {
    const valueNow = question.answer?.valueNow || 0;
    const valueFuture = question.answer?.valueFuture || 0;
    return valueFuture - valueNow;
  }

  /**
   * Obtiene la prioridad de una pregunta
   */
  getPRDQuestionPriority(question: any): string {
    return question.priority || '';
  }

  /**
   * Obtiene el ranking de una pregunta
   */
  getPRDQuestionRanking(question: any): string {
    return question.ranking || '';
  }

  /**
   * Obtiene todas las preguntas con prioridad Alta
   */
  getPRDHighPriorityQuestions(): any[] {
    const highPriorityQuestions: any[] = [];
    
    if (!this.prdTests || !Array.isArray(this.prdTests)) {
      return highPriorityQuestions;
    }
    
    this.prdTests.forEach(test => {
      test.dimensions.forEach((dimension: any) => {
        dimension.questions.forEach((question: any) => {
          if (this.getPRDQuestionPriority(question) === 'Alta') {
            highPriorityQuestions.push({
              question: question,
              dimension: dimension,
              test: test,
              gap: this.calculatePRDGap(question)
            });
          }
        });
      });
    });
    
    return highPriorityQuestions;
  }

  /**
   * Obtiene la clase CSS para el badge de brecha
   */
  getPRDGapClass(gap: number): string {
    if (gap <= 3) return 'gap-low';
    if (gap <= 6) return 'gap-medium';
    return 'gap-high';
  }

}
